import Mirage, { Collection, _routeHandlersShorthandsBase as BaseShorthandRouteHandler, Response } from 'miragejs';
import { faker } from '@faker-js/faker';
import { pluralize } from 'ember-inflector';
import config from 'garaje/config/environment';
import mirageHelper from 'garaje/mirage/helpers/mirage-helper';
import _intersection from 'lodash/intersection';
import { isPresent, isBlank } from '@ember/utils';
import moment from 'moment-timezone';
import s3PayloadForRequest from 'garaje/mirage/helpers/s3-payload-for-request';
import blobToBase64 from 'garaje/mirage/helpers/blob-to-base-64';
import { setCookie } from 'garaje/mirage/helpers/cookies';
import { endOfDay, isBefore, parse, startOfDay } from 'date-fns';
import Papa from 'papaparse';
import { FLOW_TYPE } from 'garaje/utils/enums';
import { getVisitorContactsForTenant } from 'garaje/mirage/utils/get-visitor-contacts-for-tenant';
import { getConnectedTenantsForFilter } from 'garaje/mirage/utils/get-connected-tenants-for-filter';
import { compare } from '@ember/utils';
import { paginatedResponse } from 'garaje/mirage/helpers/paginated-response';
import sortCollection from 'garaje/mirage/helpers/sort-collection';
import { getLegacyRoleName } from 'garaje/utils/custom-roles';
import { EMPLOYEE } from 'garaje/utils/roles';

const processAgreementDocuments = (attrs) => {
  if (attrs.document?.id) {
    attrs.document = {
      ...attrs.document,
      'document-url': faker.internet.url(),
      'updated-at': new Date().toISOString(),
      filename: 'sample_explain.pdf',
    };
  } else if (attrs.document?.id === null) {
    // delete
    attrs.document = null;
  }

  if (attrs.customTranslations.document) {
    Object.entries(attrs.customTranslations.document).forEach(([key]) => {
      if (!attrs.customTranslations.document) attrs.customTranslations.document = {};
      if (!attrs.customTranslations.document[key]) attrs.customTranslations.document[key] = {};
      const document = attrs.customTranslations.document[key];

      if (document.id) {
        attrs.customTranslations.document[key] = {
          ...document,
          'document-url':
            'https://www.adobe.com/support/products/enterprise/knowledgecenter/media/c4611_sample_explain.pdf',
          'updated-at': new Date().toISOString(),
          filename: 'sample_explain.pdf',
        };
      } else if (document.id === null) {
        // delete
        attrs.customTranslations.document[key] = null;
      }
    });
  }
};

// Adds support for filters on ids, like /agreement-pages?filter[]id=1&id=2
const filteredGet = function filteredGet(key) {
  return function (schema, request) {
    let collection;
    if (request.queryParams['filter[id]']) {
      collection = schema[key].find(request.queryParams['filter[id]'].split(','));
    } else {
      collection = schema[key].all();
    }
    return collection;
  };
};

// returns the base 64 im\age string to a attachment signed id
const getImageUrlForFileID = async (schema, file) => {
  const [directUpload] = schema.directUploads.where({ signed_id: file }).models;
  const [s3Upload] = schema.s3Uploads.where({ id: directUpload.key }).models;

  return blobToBase64(s3Upload.attrs.blob);
};

const getFilenameForFileID = async (schema, file) => {
  const [directUpload] = schema.directUploads.where({ signed_id: file }).models;

  return directUpload?.filename || file;
};

const getIncludedNotificationLogs = (logs) => {
  logs = logs ?? [];
  return {
    relationship: {
      data: logs.map(({ id }) => {
        return {
          id,
          type: 'notification-logs',
        };
      }),
    },
    included: logs.map(({ id, createdAt, successful }) => {
      return {
        type: 'notification-logs',
        id,
        attributes: {
          'created-at': createdAt,
          successful,
        },
      };
    }),
  };
};

/**
 * Sets up routes used by visitors
 */
export default function () {
  this.urlPrefix = '';

  // **************************************************************************
  // User Document Direct Uploads
  // to simulate latency, put { timing: 4000 } after callback function
  // e.g. this.put('route', () => {}, { timing: 4000 });
  // **************************************************************************

  this.post('/a/visitors/api/direct-uploads', (_schema, request) => {
    return new Mirage.Response(200, {}, s3PayloadForRequest(this, request));
  });

  this.put('/s3/bucket/envoy-development/:id', function (schema, request) {
    const { id } = request.params;
    const blob = request.requestBody;
    const record = schema.s3Uploads.find(id) ?? schema.s3Uploads.create({ id });

    record.update({ blob });

    return new Mirage.Response(200, {});
  });

  this.urlPrefix = config.apiHost;

  // **************************************************************************
  // Agreements
  // **************************************************************************
  this.get('/a/visitors/api/v3/agreements/:id');
  this.patch('/a/visitors/api/v3/agreements/:id', function (schema, request) {
    const id = request.params.id;
    const attrs = this.normalizedRequestAttrs();

    processAgreementDocuments(attrs);

    const agreement = schema.agreements.find(id).update(attrs);
    return agreement;
  });

  this.post('/a/visitors/api/v3/agreements', async function (schema) {
    const attrs = this.normalizedRequestAttrs();
    processAgreementDocuments(attrs);

    const agreement = schema.agreements.create(attrs);
    return agreement;
  });
  this.del('/a/visitors/api/v3/agreements/:id');

  // **************************************************************************
  // Agreement pages
  // **************************************************************************
  this.get('/a/visitors/api/v3/agreement-pages/:id');
  this.patch('/a/visitors/api/v3/agreement-pages/:id');
  this.get('/a/visitors/api/v3/agreement-pages', filteredGet('agreementPages'));

  // **************************************************************************
  // Analytics
  // **************************************************************************
  this.get('/a/visitors/api/v2/analytics/average-monthly-visitor-count');
  this.get('/a/visitors/api/v2/analytics/visitors-landing-page/:location_id', function (_schema, _request) {
    return new Mirage.Response(
      200,
      {},
      {
        data: {
          'currently-signed-in': faker.number.int({ min: 0, max: 202 }),
          'currently-signed-in-change': faker.helpers.arrayElement([-20, -54, -6, null, 33, 67, 88]),
          'expected-today': faker.number.int({ min: 0, max: 184 }),
          'expected-today-change': faker.helpers.arrayElement([-20, -54, -6, null, 33, 67, 88]),
          'visitors-weekly-change': faker.helpers.arrayElement([-20, -54, -6, null, 33, 67, 88]),
        },
      },
    );
  });

  // **************************************************************************
  // Attendees
  // **************************************************************************
  this.get('/a/visitors/api/v3/attendees/:id');
  this.patch('/a/visitors/api/v3/attendees/:id');

  // **************************************************************************
  // Authentication
  // **************************************************************************

  this.delete('users/sign_out', function (_schema, { _requestBody }) {
    setCookie('company_id', '');

    return new Mirage.Response(204);
  });

  // **************************************************************************
  // Badges
  // **************************************************************************
  this.get('/a/visitors/api/v2/badges');
  this.get('/a/visitors/api/v2/badges/:id');
  this.patch('/a/visitors/api/v2/badges/:id');

  this.get('/a/visitors/api/v3/property-badges', function ({ badges }, request) {
    const propertyId = request.queryParams['filter[property]'];
    const propertyBadges = badges.where({ propertyId });
    const response = this.serializerOrRegistry.serialize(propertyBadges, request);

    response.data.forEach((badge) => {
      badge.type = 'property-badges';
    });

    return response;
  });

  this.patch('/a/visitors/api/v3/property-badges/:id', function ({ badges }, request) {
    const id = request.params.id;
    const attrs = this.normalizedRequestAttrs();

    const badge = badges.find(id).update(attrs);

    const response = this.serializerOrRegistry.serialize(badge, request);
    response.data.type = 'property-badges';

    return response;
  });

  this.post('/a/visitors/api/v3/badge-print-jobs', function () {
    return new Response(201, {}, {});
  });

  // **************************************************************************
  // Blacklist filters
  // **************************************************************************
  this.get('/a/visitors/api/v3/blacklist-filters', function ({ locations, blacklistFilters }, { queryParams }) {
    const companyId = queryParams['filter[company]'];
    const propertyId = queryParams['filter[property]'];
    let collection = blacklistFilters.all();
    if (isPresent(companyId)) {
      collection = collection.filter(
        (blacklistFilter) => companyId === locations.find(blacklistFilter.attrs.locationId).company.id,
      );
    }
    if (isPresent(propertyId)) {
      collection = collection.filter((blacklistFilter) => propertyId === blacklistFilter.propertyId);
    }
    return collection;
  });
  this.get('/a/visitors/api/v3/blacklist-filters/:id');
  this.post('/a/visitors/api/v3/blacklist-filters');
  this.patch('/a/visitors/api/v3/blacklist-filters/:id');
  this.del('/a/visitors/api/v3/blacklist-filters/:id');

  // **************************************************************************
  // Companies
  // **************************************************************************
  this.get('/a/visitors/api/v2/companies');
  this.get('/a/visitors/api/v2/companies/:id', function (schema, request) {
    const company = schema.companies.find(request.params.id);
    const response = this.serializerOrRegistry.serialize(company, request);

    if (request.queryParams['counts-meta']) {
      response.meta = {
        'employees-count': schema.employees.where({ deleted: false }).models.length,
        'active-locations-count': schema.locations.where({ disabled: false }).models.length,
      };
    }

    return new Response(200, {}, response);
  });
  this.patch('/a/visitors/api/v2/companies/:id');

  this.get('/a/visitors/api/v2/companies/:companyId/relationships/subscription', (schema, request) => {
    return schema.subscriptions.first({
      companyId: request.params.companyId,
    });
  });
  this.get('/a/visitors/api/v3/locations/:id/kiosk-force-upgrade-message', function () {
    return new Mirage.Response(200, {}, { data: {} });
  });

  this.get('/a/visitors/api/v3/companies/:id/company-configuration', function ({ companyConfigurations }, request) {
    return companyConfigurations.findBy({ companyId: request.params.id });
  });
  this.get('/a/visitors/api/v3/company-configurations/:id');
  this.patch('/a/visitors/api/v3/company-configurations/:id');

  // **************************************************************************
  // Company roles
  // **************************************************************************
  this.get('/a/visitors/api/v2/company-roles', function (schema, request) {
    request.queryParams.include = 'user,company';
    return schema.companyRoles.all();
  });
  this.post('/a/visitors/api/v2/company-roles');
  this.patch('/a/visitors/api/v2/company-roles/:id');
  this.delete('/a/visitors/api/v2/company-roles/:id');
  this.get('/a/visitors/api/v2/company-roles/:id', function (schema, request) {
    request.queryParams.include = 'user,company';
    return schema.companyRoles.find(request.params.id);
  });

  // **************************************************************************
  // Configs
  // **************************************************************************

  this.get('/api/v1/configs');
  this.get('/api/v1/configs/:id');

  this.get('/a/visitors/api/v2/configs');
  this.get('/a/visitors/api/v2/configs/:id');
  this.patch('/a/visitors/api/v2/configs/:id', function ({ configs }, request) {
    const id = request.params.id;
    const attrs = this.normalizedRequestAttrs();

    const config = configs.find(id).update(attrs);

    const response = this.serializerOrRegistry.serialize(config, request);

    if (config.property) response.data.type = 'property-device-configs';
    return response;
  });
  this.post('/a/visitors/api/v2/configs/:id/refresh', () => new Response(204));
  this.post('/a/visitors/api/v2/configs/:id/attachments/welcome-image', function ({ configs }, request) {
    const id = request.params.id;
    const config = configs.find(id).update({ welcomeImage: 'https://picsum.photos/400/200' });

    const response = this.serializerOrRegistry.serialize(config, request);

    if (config.property) response.data.type = 'property-device-configs';
    return response;
  });
  this.delete('/a/visitors/api/v2/configs/:id/attachments/welcome-image', function ({ configs }, request) {
    const id = request.params.id;
    const config = configs.find(id).update({ welcomeImage: null });

    const response = this.serializerOrRegistry.serialize(config, request);

    if (config.property) response.data.type = 'property-device-configs';
    return response;
  });

  // **************************************************************************
  // Zone Configs
  // **************************************************************************

  this.get('/a/visitors/api/v3/property-device-configs', function ({ configs }, request) {
    let collection = configs.all().models.filter((config) => !!config.property);
    const propertyId = request.queryParams['filter[property]'];
    if (propertyId) collection = configs.where({ propertyId });

    const response = this.serializerOrRegistry.serialize(collection, request);
    response.data.forEach((config) => {
      config.type = 'property-device-configs';
    });

    return response;
  });

  // **************************************************************************
  // Badges
  // **************************************************************************
  this.get('/a/visitors/api/v3/badges/:id');
  this.patch('/a/visitors/api/v3/badges/:id');
  this.get('/a/visitors/api/v3/badges', filteredGet('badges'));

  // **************************************************************************
  // Devices
  // **************************************************************************
  this.get('/api/v1/devices/:id/integrations', function (_schema, _request) {
    return new Mirage.Response(
      200,
      { 'Content-Type': 'application/json' },
      {
        integrations: [],
      },
    );
  });

  this.get('/a/visitors/api/v3/devices/:id');
  this.post('/a/visitors/api/v3/devices');
  this.patch('/a/visitors/api/v3/devices/:id', function (schema, request) {
    const device = schema.devices.find(request.params.id);
    const attrs = this.normalizedRequestAttrs();

    // the Device->Printer relationship is polymorphic on the ember-data side
    // but not on the Mirage side; paper over that so it can update relationships.
    const hasPropertyPrinterRelationship = attrs.printerId?.type === 'property-printers';
    if (hasPropertyPrinterRelationship) {
      attrs.printerId.type = 'printers';
    }
    device.update(attrs);
    const response = this.serialize(device);
    if (hasPropertyPrinterRelationship) {
      response.data.relationships.printer.data.type = 'property-printers';
    }
    return response;
  });
  this.delete('/a/visitors/api/v3/devices/:id');

  // **************************************************************************
  // Emails
  // **************************************************************************
  this.get('/a/visitors/api/v2/emails');
  this.post('/a/visitors/api/v2/emails');
  this.get('/a/visitors/api/v2/emails/:id');
  this.delete('/a/visitors/api/v2/emails/:id');

  // **************************************************************************
  // Employee locations
  // **************************************************************************
  this.get('/a/visitors/api/v3/employee-locations');
  this.get('/a/visitors/api/v3/employee-locations/:id');
  this.delete('/a/visitors/api/v3/employee-locations/:id');
  this.post('/a/visitors/api/v3/employee-locations');

  // **************************************************************************
  // Employee groups (SCIM)
  // **************************************************************************
  this.get('/a/visitors/api/v3/employee-groups');

  // **************************************************************************
  // Employee group roles (SCIM)
  // **************************************************************************
  this.get('/a/visitors/api/v3/employee-group-roles');
  this.post('/a/visitors/api/v3/employee-group-roles');
  this.patch('/a/visitors/api/v3/employee-group-roles/:id');
  this.delete('/a/visitors/api/v3/employee-group-roles/:id');

  // **************************************************************************
  // Employees
  // **************************************************************************
  this.get('/a/visitors/api/v3/employees', function (schema, request) {
    let collection;

    if (request.queryParams['filter[id]']) {
      collection = schema.employees.find(request.queryParams['filter[id]'].split(','));
    } else {
      collection = schema.employees.all();
    }

    let employees = collection.models;

    if (request.queryParams['filter[locations]']) {
      const locationIds = request.queryParams['filter[locations]'].split(',');
      employees = employees.filter((employee) => {
        const employeeLocationIds = employee.locationIds.map((id) => id.toString());
        return isPresent(_intersection(locationIds, employeeLocationIds));
      });
    }

    if (request.queryParams['filter[email]']) {
      const matcher = new RegExp(request.queryParams['filter[email]'], 'gi');
      employees = employees.filter((employee) => matcher.test(employee.email));
    }

    if (request.queryParams['filter[email-in]']) {
      const emails = request.queryParams['filter[email-in]'].split(',');
      employees = employees.filter((employee) => emails.includes(employee.email));
    }

    if (request.queryParams['filter[excludeHidden]'] === 'true') {
      employees = employees.filter((employee) => !employee.hideFromHostList);
    }

    if (request.queryParams['filter[deleted]'] === 'true') {
      employees = employees.filterBy('deleted');
    } else {
      employees = employees.filter((employee) => !employee.deleted);
    }

    const queryFilter = request.queryParams['filter[query]'];
    if (queryFilter) {
      if (request.queryParams['prefix'] === 'true') {
        employees = employees.filter(({ name }) => name.toLowerCase().startsWith(queryFilter.toLowerCase()));
      } else {
        employees = employees.filter(({ name }) => name.toLowerCase().includes(queryFilter.toLowerCase()));
      }
    }

    collection.models = employees;

    // TODO factor this out in a paginated helper

    const response = this.serializerOrRegistry.serialize(collection, request);
    response.meta = {
      total: employees.length,
    };

    response.data.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));
    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    return new Mirage.Response(200, {}, response);
  });

  this.post('/a/visitors/api/v3/employees');
  this.get('/a/visitors/api/v3/employees/:id');
  this.patch('/a/visitors/api/v3/employees/:id');

  this.delete('/a/visitors/api/v3/employees/:id', (schema, request) => {
    this.db.employees.update(request.params.id, {
      deleted: true,
    });

    return new Mirage.Response(204);
  });

  this.get('/a/visitors/api/v3/scheduled-employees', (schema, request) => {
    const scheduledEmployees = schema.scheduledEmployees.all();
    const response = this.serializerOrRegistry.serialize(scheduledEmployees, request);

    response.meta = { total: scheduledEmployees.models.length };

    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }
    return new Mirage.Response(200, {}, response);
  });

  this.post('/a/visitors/api/v3/employees/:id/invite', ({ employees, users }, { params }) => {
    const employee = employees.find(params.id);
    const user = users.create({ employees: [employee], fullName: employee.name, email: employee.email });
    return user;
  });

  this.get('/a/visitors/api/v3/connect-employees', function (schema, request) {
    const _tenants = getConnectedTenantsForFilter(request, schema);
    const queryNameFilter = request.queryParams['filter[query-name]'];
    const roleTypeFilter = request.queryParams['filter[tenant-roles.role-type]']?.split(',');

    schema.employees.all().models.forEach((employee) => {
      const tenants = employee.locationIds
        .map((id) => _tenants.find((_tenant) => _tenant.location.id === id))
        .filter(Boolean);

      if (tenants.length) {
        let connectVisitorContacts = [];
        const tenantRoles = [];

        tenants.forEach((tenant) => {
          connectVisitorContacts = getVisitorContactsForTenant({
            tenant,
            schema,
            isConnect: true,
            user: employee.user,
          });
        });

        employee.user.companyRoles.models.forEach((companyRole) => {
          const tenant = tenants.find((tenant) => tenant.location.companyId === companyRole.companyId);
          tenantRoles.push(
            schema.create('tenant-role', {
              id: `company-role-${companyRole.id}`,
              tenantId: tenant.id,
              roleName: companyRole.roleName,
              roleType: companyRole.roleName === EMPLOYEE ? companyRole.roleName : 'Tenant Admin',
            }),
          );
        });

        employee.user.locationRoles.models.forEach((locationRole) => {
          const tenant = tenants.find((tenant) => tenant.location.id === locationRole.location.id);
          tenantRoles.push(
            schema.create('tenant-role', {
              id: `location-role-${locationRole.id}`,
              tenantId: tenant.id,
              roleName: locationRole.roleName,
              roleType: locationRole.roleName === EMPLOYEE ? locationRole.roleName : 'Tenant Admin',
            }),
          );
        });

        employee.user.zoneRoles.models.forEach((zoneRole) => {
          tenantRoles.push(
            schema.create('tenant-role', {
              id: `zone-role-${zoneRole.id}`,
              tenantId: tenants[0].id,
              roleName: zoneRole.roleName,
              roleType: 'Property Admin',
            }),
          );
        });

        schema.create('connect-employee', {
          id: employee.id,
          name: employee.name,
          profilePictureUrl: employee.profilePictureUrl,
          tenantRoles,
          connectVisitorContacts,
        });
      }
    });

    let results = schema.connectEmployees.all();

    if (request.queryParams.sort) {
      let sortParam = request.queryParams.sort;
      let sortDirectionFlag = 1; // 1 for ascending, -1 for descending
      if (sortParam.startsWith('-')) {
        sortDirectionFlag = -1;
        sortParam = sortParam.substring(1);
      }
      results = results.sort((a, b) => sortDirectionFlag * compare(a[sortParam], b[sortParam]));
    }

    if (queryNameFilter) {
      results = results.filter(({ name }) => name.toLowerCase().includes(queryNameFilter.toLowerCase()));
    }

    if (roleTypeFilter) {
      results = results.filter(({ tenantRoles }) =>
        tenantRoles.models.some(({ roleType }) => roleTypeFilter.includes(roleType)),
      );
    }

    const response = paginatedResponse(this, results, request);

    schema.connectEmployees.all().destroy();
    schema.connectVisitorContacts.all().destroy();
    schema.tenantRoles.all().destroy();

    return response;
  });

  // **************************************************************************
  // Entries
  // **************************************************************************
  this.post('/a/visitors/api/v2/entries');
  this.patch('/a/visitors/api/v2/entries/:id');
  this.get('/a/visitors/api/v2/entries/:id', function ({ entries }, request) {
    const entry = entries.find(request.params.id);

    const response = this.serializerOrRegistry.serialize(entries.find(request.params.id), request);

    if (request.queryParams.include?.includes('invite-multi-tenancy-visitor-notification-logs')) {
      const { relationship, included } = getIncludedNotificationLogs(
        entry.invite?.multiTenancyVisitorNotificationLogs.models ?? [],
      );

      response.data.relationships['invite-multi-tenancy-visitor-notification-logs'] = relationship;
      response.included = included;
    }

    return response;
  });
  this.get('/a/visitors/api/v2/entries', function (schema, request) {
    const getSortFieldValue = function (object, sortField) {
      switch (sortField) {
        case 'approvalChecksStatus':
          return object.approvalStatus.status;
        case 'bulkInvitesGroupName':
          return object.groupName;
        default:
          return object[sortField];
      }
    };
    let entries = mirageHelper.sortable('entries', getSortFieldValue)(schema, request);

    if (config.mirageEntriesDateFilter) {
      if (request.queryParams['filter[start-date]']) {
        const startDate = moment(request.queryParams['filter[start-date]']);
        entries = entries.filter((entry) => moment(entry.signedInAt) >= startDate);
      }

      if (request.queryParams['filter[end-date]']) {
        const endDate = moment(request.queryParams['filter[end-date]']);
        entries = entries.filter((entry) => moment(entry.signedInAt) <= endDate);
      }
    }
    if (request.queryParams['filter[visitor_type]']) {
      const flowName = request.queryParams['filter[visitor_type]'];
      if (Array.isArray(flowName)) {
        entries = entries.filter((entry) => flowName.includes(entry.flowName));
      } else {
        entries = entries.filter((entry) => entry.flowName === flowName);
      }
    }

    const response = this.serializerOrRegistry.serialize(entries, request);

    response.meta = {
      total: entries.models.length,
    };

    if (request.queryParams.include?.includes('platform-jobs')) {
      response.included = [];
      const entryIds = entries.models.map((entry) => entry.id);
      const platformJobs = schema.platformJobs.all().filter((platformJob) => {
        return entryIds.includes(platformJob.entryId);
      });
      if (platformJobs.length) {
        response.included = this.serializerOrRegistry.serialize(platformJobs, request).data;
      }
    }

    if (request.queryParams.include?.includes('invite-multi-tenancy-visitor-notification-logs')) {
      response.data.forEach((entry) => {
        const { relationship, included } = getIncludedNotificationLogs(
          schema.entries.find(entry.id).invite?.multiTenancyVisitorNotificationLogs?.models,
        );

        entry.relationships['invite-multi-tenancy-visitor-notification-logs'] = relationship;

        response.included = [...(response.included ?? []), ...included];
      });
    }

    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    return new Mirage.Response(200, {}, response);
  });
  this.delete('/a/visitors/api/v2/entries/:id', function (schema, request) {
    const entry = schema.entries.find(request.params.id);

    if (request.requestBody && JSON.parse(request.requestBody)['delete-group']) {
      entry.additionalGuestEntries.models.forEach(function (e) {
        e.destroy();
      });
    } else {
      entry.additionalGuestEntries.models.forEach(function (e) {
        e.groupParentId = null;
        e.save();
      });
    }

    entry.destroy();

    return new Mirage.Response(204);
  });

  this.post('/a/visitors/api/v2/entries/:id/review', function ({ entries }, request) {
    const entry = entries.find(request.params.id);
    const body = JSON.parse(request.requestBody);

    if (body.action === 'approve') {
      entry.approvalStatus.status = 'approved';
    } else if (body.action === 'deny') {
      entry.approvalStatus.status = 'denied';
    }
    entry.approvalStatus['reviewed-at'] = moment().format();
    entry.approvalStatus['reviewer-name'] = 'Fixme Inmirage'; // this should be the current user, derived from the JWT sub, which at the time of writing is absent in the JWT for unknown reasons
    return new Mirage.Response(204);
  });

  this.get('/a/visitors/api/v3/entries/:id/previous-entries', () => {
    const response = {
      data: [],
      links: {},
    };
    return new Mirage.Response(200, {}, response);
  });

  this.get('/a/visitors/api/v2/entries/:id/signed-agreements', function (_response, request) {
    const response = {
      data: {
        id: request.params.id,
        type: 'signed-agreements',
        attributes: {
          status: 'completed',
          url: 'https://placekitten.com/200/300',
        },
      },
    };

    return new Mirage.Response(200, {}, response);
  });

  // **************************************************************************
  // Entry export jobs
  // **************************************************************************
  this.get('/a/visitors/api/v2/entry-export-jobs/:id');
  this.post('/a/visitors/api/v2/entry-export-jobs');

  // **************************************************************************
  // Fallback contacts
  // **************************************************************************
  this.get('/a/visitors/api/v3/fallback-contacts');
  this.post('/a/visitors/api/v3/fallback-contacts');
  this.del('/a/visitors/api/v3/fallback-contacts/:id');

  // **************************************************************************
  // Flows
  // **************************************************************************
  this.post('/a/visitors/api/v3/flows');
  this.patch('/a/visitors/api/v3/flows/:id');
  this.get('/a/visitors/api/v3/flows/:id/sign-in-field-action-groups');

  this.delete('/a/visitors/api/v3/flows/:id', function (schema, { params: { id } }) {
    const flow = schema.flows.find(id);
    const { location } = flow;

    // TODO: @heroiceric Look into possible bug with mirage not removing
    // relationship references when child is deleted.
    //
    // This this removes the relationship between the flow that we are deleting
    // and its location. This should happen automatically but it does not and
    // fails with `Mirage: You're instantiating a location that has a flowIds of
    // 1,2, but some of those records don't exist in the database.`
    //
    // I am pretty sure this is related to our usage of fixtures.
    const newFlowIds = location.flowIds.filter((id) => id !== flow.id);
    const newFlows = schema.flows.find(newFlowIds);

    location.flows = newFlows;
    location.save();

    flow.destroy();

    return new Response(204, {}, '');
  });

  this.get('/a/visitors/api/v3/flows', function (schema, request) {
    let collection;

    const offset = parseInt(request.queryParams['page[offset]'], 10);
    const limit = parseInt(request.queryParams['page[limit]'], 10);
    const typeParam = request.queryParams['filter[type]'];
    const idParam = request.queryParams['filter[id]'];
    const locationIdsParam = request.queryParams['filter[location-id]'];
    const locationIdParam = request.queryParams['filter[location]'];
    const nameParam = request.queryParams['filter[name]'];
    const zoneIdParam = request.queryParams['filter[zone]'];
    const propertyOwned =
      request.queryParams['filter[property_owned]'] || request.queryParams['filter[property-owned]'];

    const walkinFlow = schema.flows.findBy({ type: FLOW_TYPE.PROPERTY_WALKUP });

    if (idParam) {
      return schema.flows.find(idParam.split(','));
    }

    if (walkinFlow) {
      const tenant = schema.tenants.findBy({ locationId: walkinFlow.locationId });

      if (tenant) {
        const config = schema.connectPropertyConfigurations.findBy({ propertyId: tenant.propertyId });
        if (config) walkinFlow.update({ propertyInviteApprovalsEnabled: config.inviteApprovalsEnabled });
      }
    }

    const applyPropertyOwnedFilter = (flows) =>
      flows.filter(({ type }) => {
        if (propertyOwned === 'true') {
          // only property flows
          return [FLOW_TYPE.PROPERTY, FLOW_TYPE.PROPERTY_WALKUP].includes(type);
        } else if (propertyOwned === 'true,false') {
          // all flows, including property ones
          return true;
        } else {
          // only non-property flows
          return ![FLOW_TYPE.PROPERTY, FLOW_TYPE.PROPERTY_WALKUP].includes(type);
        }
      });

    if (locationIdParam) {
      let flows = schema.flows.where({ locationId: locationIdParam });
      flows.models.forEach((flow) => {
        const collection = new Collection('user-document-template-configuration');
        collection.models.push(...flow.userDocumentTemplateConfigurations.models.filterBy('active'));
        flow.activeUserDocumentTemplateConfigurations = collection;
      });

      flows = applyPropertyOwnedFilter(flows);

      return flows;
    }

    const whereConditions = {};

    if (typeParam && typeParam != 'all') {
      whereConditions.type = typeParam;
    }

    collection = schema.flows.where(whereConditions);
    if (zoneIdParam) {
      const propertyDeviceConfig = schema.configs.findBy({ propertyId: zoneIdParam });
      collection = propertyDeviceConfig.flows;
    }

    if (locationIdsParam) {
      const locationIds = locationIdsParam.split(',');
      collection = collection.filter((f) => locationIds.includes(f.locationId));
    }

    if (nameParam) {
      collection = collection.filter((f) => {
        const name = f.attrs.name.toLowerCase();
        const substring = nameParam.toLowerCase();
        return name.includes(substring);
      });
    }

    collection = applyPropertyOwnedFilter(collection);

    const response = this.serializerOrRegistry.serialize(collection, request);

    if (!idParam) {
      const totalCount = collection.length;

      if (offset && limit) {
        collection = collection.slice(offset, offset + limit);
      }

      response.meta = { total: totalCount, limit, offset };
    }

    return new Mirage.Response(200, {}, response);
  });

  this.get('/a/visitors/api/v3/copy-flows', function (schema) {
    schema.copyFlows.all().destroy();
    schema.flows.all().models.forEach((flow) => {
      // Skip employee registration flows
      if (!flow.employeeCentric) {
        schema.copyFlows.create({
          name: flow.name,
          locationName: flow.location.name,
          companyName: flow.location.company.name,
        });
      }
    });

    return schema.copyFlows.all();
  });
  this.get('/a/visitors/api/v3/flows/:id');

  // **************************************************************************
  // Employee Registration Flows
  // **************************************************************************
  this.post('/a/visitors/api/v3/employee-screening-flows', function (schema, _request) {
    const flow = schema.create('employee-screening-flow', {
      enabled: true,
    });

    const summaryPage = schema.summaryPages.create('summary-page', {
      flow,
      message:
        '# Not approved to work in the workplace \n\nWe’re sorry, but you won’t be allowed into the workplace. Please try again tomorrow.',
      kind: 'none',
    });

    flow.summaryPage = summaryPage;

    const signInFieldPage = schema.signInFieldPages.create('sign-in-field-page', { flow });
    flow.signInFieldPage = signInFieldPage;

    schema.signInFields.create('sign-in-field', {
      signInFieldPage,
      kind: 'email',
      name: 'Your Email Address',
      localized: 'Your Email Address',
      identifier: 'email',
    });

    schema.signInFields.create('sign-in-field', {
      signInFieldPage,
      kind: 'text',
      name: 'Your Full Name',
      localized: 'Your Full Name',
      identifier: 'name',
    });

    /* TODO what about the rest like we use in factory?
    const signInFieldAction = schema.employeeSignInFieldActions.create('employee-sign-in-field-action', {
      signInField: signInField,
      action: 'alert',
      matchOperator: 'is',
      signInFieldValue: 1
    });
    schema.employeeSignInFieldActionRuleGroup.create('employee-sign-in-field-action-rule-group', {
      flow: flow,
      signInFieldActions: [signInFieldAction],
      actions: ['alert']
    });
    */
    flow.save();
    return flow;
  });

  this.patch('/a/visitors/api/v3/employee-screening-flows/:id');
  this.delete('/a/visitors/api/v3/employee-screening-flows/:id', function (schema, { params: { id } }) {
    const flow = schema.employeeScreeningFlows.find(id);
    const { location } = flow;

    // employee screening flows which aren't yet enabled won't have a location
    if (location) {
      const newFlowIds = location.flowIds.filter((id) => id !== flow.id);
      const newFlows = schema.employeeScreeningFlows.find(newFlowIds);
      location.flows = newFlows;
      location.save();
    }

    flow.destroy();

    return new Response(204, {}, '');
  });
  this.get('/a/visitors/api/v3/employee-screening-flows', function (schema, request) {
    let collection;

    const typeParam = request.queryParams['filter[type]'];
    const idParam = request.queryParams['filter[id]'];
    const locationIdParam = request.queryParams['filter[location]'];

    if (idParam) {
      collection = schema.employeeScreeningFlows.find(idParam.split(','));
    } else if (typeParam && typeParam != 'all') {
      collection = schema.employeeScreeningFlows.where({ type: typeParam });
    } else if (locationIdParam) {
      collection = schema.employeeScreeningFlows.where({ locationId: locationIdParam });
    } else {
      collection = schema.employeeScreeningFlows.all();
    }
    return collection;
  });
  this.get('/a/visitors/api/v3/employee-screening-flows/:id');

  // **************************************************************************
  // GDPR configurations
  // **************************************************************************
  this.post('/a/visitors/api/v3/gdpr-configurations');
  this.patch('/a/visitors/api/v3/gdpr-configurations/:id');
  this.get('/a/visitors/api/v3/gdpr-configurations/:id');

  this.get('/a/visitors/api/v3/property-gdpr-configurations', function ({ gdprConfigurations }, request) {
    const propertyId = request.queryParams['filter[property]'];
    const configurations = gdprConfigurations.where({ propertyId });

    const response = this.serializerOrRegistry.serialize(configurations, request);
    response.data.forEach((config) => {
      config.type = 'property-gdpr-configurations';
    });

    return response;
  });

  this.post('/a/visitors/api/v3/property-gdpr-configurations', function ({ gdprConfigurations }, request) {
    const props = this.normalizedRequestAttrs();
    const config = gdprConfigurations.create(props);

    const response = this.serializerOrRegistry.serialize(config, request);
    response.data.type = 'property-gdpr-configurations';

    return response;
  });

  this.patch('/a/visitors/api/v3/property-gdpr-configurations/:id', function ({ gdprConfigurations }, request) {
    const props = this.normalizedRequestAttrs();
    const config = gdprConfigurations.find(props.id);
    config.update(props);

    const response = this.serializerOrRegistry.serialize(config, request);
    response.data.type = 'property-gdpr-configurations';

    return response;
  });

  // **************************************************************************
  // Global setting batches
  // **************************************************************************
  this.post('/a/visitors/api/v3/global-setting-batches', function ({ globalSettingBatches } /*, request */) {
    return globalSettingBatches.create(this.normalizedRequestAttrs());
  });

  this.post('/a/visitors/api/v3/global-setting-batches/:id/undo', function () {
    return new Mirage.Response(201, {}, { data: { id: 1, type: 'global-setting-batches' } });
  });

  // **************************************************************************
  // Integrations
  // **************************************************************************
  this.get('/a/visitors/api/v2/integrations/enabled', function () {
    return new Mirage.Response(200, {}, { data: [] });
  });

  this.get('/a/visitors/api/v2/integrations/google-apps');
  this.post('/a/visitors/api/v2/integrations/google-apps');
  this.get('/a/visitors/api/v2/integrations/google-apps/:id');
  this.patch('/a/visitors/api/v2/integrations/google-apps/:id');
  this.delete('/a/visitors/api/v2/integrations/google-apps/:id');

  this.get('/a/visitors/api/v2/integrations/ifttt-integrations');
  this.get('/a/visitors/api/v2/integrations/ifttt-integrations/:id');
  this.patch('/a/visitors/api/v2/integrations/ifttt-integrations/:id');
  this.delete('/a/visitors/api/v2/integrations/ifttt-integrations/:id');

  this.get('/a/visitors/api/v2/integrations/salesforce-integrations');
  this.post('/a/visitors/api/v2/integrations/salesforce-integrations');
  this.get('/a/visitors/api/v2/integrations/salesforce-integrations/:id');
  this.patch('/a/visitors/api/v2/integrations/salesforce-integrations/:id');
  this.delete('/a/visitors/api/v2/integrations/salesforce-integrations/:id');

  this.get('/a/visitors/api/v2/integrations/zapier-integrations');
  this.post('/a/visitors/api/v2/integrations/zapier-integrations');
  this.get('/a/visitors/api/v2/integrations/zapier-integrations/:id');
  this.patch('/a/visitors/api/v2/integrations/zapier-integrations/:id');
  this.delete('/a/visitors/api/v2/integrations/zapier-integrations/:id');

  this.get('/a/visitors/api/v2/integrations/okta');
  this.post('/a/visitors/api/v2/integrations/okta');
  this.get('/a/visitors/api/v2/integrations/okta/:id', function (schema, request) {
    return schema.oktaIntegrations.find(request.params.id);
  });
  this.patch('/a/visitors/api/v2/integrations/okta/:id');
  this.delete('/a/visitors/api/v2/integrations/okta/:id');

  this.get('/a/visitors/api/v3/scim-groups/:id');

  this.get('/a/visitors/api/v3/scim-data', function () {
    return new Mirage.Response(
      200,
      {},
      {
        data: [
          {
            id: '12345',
            type: 'scim-data',
            attributes: {
              departments: ['engineers'],
              divisions: ['engines', 'engrams'],
              organizations: ['eng-org'],
              groups: [
                { id: '23', name: 'Engineering' },
                { id: '24', name: 'Engineering 2' },
              ],
            },
          },
        ],
      },
    );
  });

  this.post('/a/visitors/api/v3/scim-integrations/:id/regenerate-token', function (schema, request) {
    const scimIntegration = schema.scimIntegrations.find(request.params.id);

    scimIntegration.accessToken = `secret-access-token-${faker.string.uuid()}`;
    scimIntegration.state = 'pending';

    scimIntegration.save();

    return scimIntegration;
  });
  this.get('/a/visitors/api/v3/scim-integrations/:id');
  this.patch('/a/visitors/api/v3/scim-integrations/:id');
  this.delete('/a/visitors/api/v3/scim-integrations/:id');
  this.post('/a/visitors/api/v3/scim-integrations', function (schema, request) {
    const handler = new BaseShorthandRouteHandler(schema, this.serializerOrRegistry);

    const scimIntegration = schema.scimIntegrations.create(handler._getAttrsForRequest(request, 'scim-integration'));

    scimIntegration.accessToken = `secret-access-token-${faker.string.uuid()}`;
    scimIntegration.state = 'pending';

    scimIntegration.save();

    return scimIntegration;
  });
  this.get('/a/visitors/api/v2/integrations/one-logins');
  this.post('/a/visitors/api/v2/integrations/one-logins');
  this.get('/a/visitors/api/v2/integrations/one-logins/:id');
  this.patch('/a/visitors/api/v2/integrations/one-logins/:id');
  this.delete('/a/visitors/api/v2/integrations/one-logins/:id');

  this.post('/a/visitors/api/v2/integrations/slack-integrations');
  this.get('/a/visitors/api/v2/integrations/slack-integrations/:id');
  this.delete('/a/visitors/api/v2/integrations/slack-integrations/:id');

  // **************************************************************************
  // Invites
  // **************************************************************************
  this.get('/a/visitors/api/v2/invites/:id');
  this.post('/a/visitors/api/v2/invites');
  this.patch('/a/visitors/api/v2/invites/:id');
  this.delete('/a/visitors/api/v2/invites/:id');
  this.post('/a/visitors/api/v3/invites/:id/notify', {}, 204);
  this.get('/a/visitors/api/v3/invites');
  this.patch('/a/visitors/api/v3/connect-invites/:id', function ({ invites }) {
    const attrs = this.normalizedRequestAttrs();
    const invite = invites.find(attrs.id);
    return invite.update(attrs);
  });

  this.get('/a/visitors/api/v3/connect-invites/:id', function ({ invites }, request) {
    const { id } = request.params;
    return invites.find(id);
  });

  this.delete('/a/visitors/api/v3/connect-invites/:id', function ({ invites }, request) {
    const { id } = request.params;
    const invite = invites.find(id);
    invite.destroy();

    return new Response(204);
  });

  this.post('/a/visitors/api/v3/connect-invites/:id/check-in', function ({ invites, notificationLogs }, request) {
    const invite = invites.find(request.params.id);
    const body = JSON.parse(request.requestBody) ?? {};

    invite.update({
      checkedInAt: body['checked-in-at'],
      multiTenancyVisitorNotificationLogs: [notificationLogs.create()],
    });

    return invite;
  });

  this.get('/a/visitors/api/v3/connect-invites', function (schema, request) {
    let invites = schema.invites.all();

    if (request.queryParams['filter[tenant]']) {
      const tenantIds = request.queryParams['filter[tenant]'].split(',');
      invites = invites.filter((invite) => tenantIds.includes(invite.tenantId));
    }

    if (request.queryParams['filter[suites]']) {
      const suiteIds = request.queryParams['filter[suites]'].split(',');
      invites = invites.filter((invite) => {
        const inviteSuiteIds = invite.suiteIds;
        return suiteIds.any((suiteId) => inviteSuiteIds.includes(suiteId));
      });
    }

    if (request.queryParams['filter[property]']) {
      const property = request.queryParams['filter[property]'];
      invites = invites.filter((invite) => property === invite.tenant?.propertyId);
    }

    if (request.queryParams['filter[fullName]']) {
      const fullName = request.queryParams['filter[fullName]'].toLowerCase();
      invites = invites.filter((invite) => invite.fullName.toLowerCase().includes(fullName));
    }

    if (request.queryParams['filter[date-from]']) {
      const dateFrom = request.queryParams['filter[date-from]'];
      invites = invites.filter(
        (invite) => new Date(invite.expectedArrivalTime) >= startOfDay(parse(dateFrom, 'yyyy-MM-dd', new Date())),
      );
    }

    if (request.queryParams['filter[date-to]']) {
      const dateTo = request.queryParams['filter[date-to]'];
      invites = invites.filter(
        (invite) => new Date(invite.expectedArrivalTime) <= endOfDay(parse(dateTo, 'yyyy-MM-dd', new Date())),
      );
    }

    if (request.queryParams['filter[status]']) {
      const status = request.queryParams['filter[status]'];

      invites = invites.filter(
        (invite) => !!invite.checkedInAt === { 'checked-in': true, 'not-checked-in': false }[status],
      );
    }

    const response = this.serializerOrRegistry.serialize(invites, request);
    response.meta = { total: invites.length };

    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    return new Mirage.Response(200, {}, response);
  });

  this.patch('/a/visitors/api/v3/connect-invites/:id', function ({ invites }) {
    const attrs = this.normalizedRequestAttrs();
    const invite = invites.find(attrs.id);
    return invite.update(attrs);
  });

  this.delete('/a/visitors/api/v3/connect-invites/:id', function ({ invites }, request) {
    const { id } = request.params;
    const invite = invites.find(id);
    invite.destroy();

    return new Response(204);
  });

  this.post('/a/visitors/api/v3/bulk-connect-invites', function ({ invites, bulkConnectInvites }, request) {
    const { tenantId, fullNames, groupName, checkedInAt, expectedArrivalTime, propertyNotes } =
      this.normalizedRequestAttrs();

    const ids = [];

    fullNames.forEach((fullName) => {
      ids.push(
        invites.create({
          fullName,
          tenantId,
          groupName,
          checkedInAt,
          expectedArrivalTime,
          propertyNotes,
          visitorCategory: 'property',
        }).id,
      );
    });

    const bulkInvite = bulkConnectInvites.create({
      id: faker.string.uuid(),
      connectInvites: invites.where(({ id }) => ids.includes(id)),
    });

    const response = this.serializerOrRegistry.serialize(bulkInvite, request);
    response.data.relationships['connect-invites'].data.forEach((invite) => (invite.type = 'connect-invites'));

    const { data } = JSON.parse(request.requestBody);
    response.data.attributes = data.attributes;

    return response;
  });

  this.get('/a/visitors/api/v2/invites', function (schema, request) {
    const getSortFieldValue = function (object, sortField) {
      return sortField === 'bulkInvitesGroupName' ? object.groupName : object[sortField];
    };
    let invites = mirageHelper.sortable('invites', getSortFieldValue)(schema, request);
    if (config.mirageInvitesDateFilter) {
      if (request.queryParams['filter[date]']) {
        const date = moment(request.queryParams['filter[date]']);
        invites = invites.filter((invite) => moment(invite.expectedArrivalTime).isSame(date, 'day'));
      }
    }

    if (request.queryParams['filter[visitor_type]']) {
      const visitorType = request.queryParams['filter[visitor_type]'];
      if (Array.isArray(visitorType)) {
        invites = invites.filter(({ flowName }) => visitorType.includes(flowName));
      } else {
        invites = invites.filter(({ flowName }) => flowName === visitorType);
      }
    }

    const approvalStatusFilter =
      request.queryParams['filter[approval_status]'] || request.queryParams['filter[approval-status]'];
    if (approvalStatusFilter) {
      invites = invites.filter(({ approvalStatus }) => approvalStatus?.status === approvalStatusFilter);
    }

    const response = this.serializerOrRegistry.serialize(invites, request);

    response.meta = { total: invites.models.length };

    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    return new Mirage.Response(200, {}, response);
  });

  this.post('/a/visitors/api/v2/invites/:id/review', function ({ invites }, request) {
    const approvalStatusString = (reports) => {
      if (!reports || reports.length === 0) return 'approved';
      // if any report is "denied" the whole invite is denied
      if (reports.any((report) => report.status === 'denied')) return 'denied';
      // if every report is "approved" the invite is approved
      if (reports.every((report) => report.status === 'approved')) return 'approved';
      // otherwise the invite is in review status
      return 'review';
    };
    const invite = invites.find(request.params.id);
    const body = JSON.parse(request.requestBody);
    const approvalStatus = invite.approvalStatus;

    const reports = approvalStatus.report || [];
    const firstReviewableReport = reports.find((report) => !report.status || report.status === 'review');
    if (body.action === 'approve') {
      // find the first report in "review" status and mark it as "approved"
      if (firstReviewableReport) {
        firstReviewableReport.status = 'approved';
      }
    } else if (body.action === 'deny') {
      // find the first report in "review" status and mark it as "denied"
      if (firstReviewableReport) {
        firstReviewableReport.status = 'denied';
      }
    }
    approvalStatus.status = approvalStatusString(reports);
    invite.update({ approvalStatus });
    invite.approvalStatus['reviewed-at'] = moment().format();
    invite.approvalStatus['reviewer-name'] = 'Fixme Inmirage'; // this should be the current user, derived from the JWT sub, which at the time of writing is absent in the JWT for unknown reasons
    return new Mirage.Response(204);
  });

  this.get('/a/visitors/api/v2/invites/:id/signed-agreements', function (_response, request) {
    const response = {
      data: {
        id: request.params.id,
        type: 'signed-agreements',
        attributes: {
          status: 'completed',
          url: 'https://placekitten.com/200/300',
        },
      },
    };

    return new Mirage.Response(200, {}, response);
  });

  this.post(
    '/a/visitors/api/v3/invites/bulk',
    function () {
      return new Mirage.Response(201, {}, { data: { attributes: { 'success-rows': [0, 1, 2], 'error-rows': [] } } });
    },
    { timing: 5000 },
  );

  this.post(
    '/a/visitors/api/v2/entries/bulk-create',
    function (schema) {
      const entry1 = schema.entries.create();
      return new Mirage.Response(201, {}, { data: [entry1] });
    },
    { timing: 5000 },
  );

  // **************************************************************************
  // LocationsCapacities
  // **************************************************************************
  this.get('/a/visitors/api/v3/locations-capacities', function ({ locationsCapacities }, { queryParams }) {
    const locationId = parseInt(queryParams['filter[location]']);
    const startDate = queryParams['filter[start-date]'];
    const capacityDate = queryParams['filter[capacity-date]'];
    const endDate = queryParams['filter[end-date]'];

    return locationsCapacities
      .where({ locationId })
      .filter((locationsCapacity) => {
        if (isPresent(capacityDate)) {
          return moment(capacityDate).startOf('day').isSame(locationsCapacity.capacityDate, 'day');
        }
        return true;
      })
      .filter((locationsCapacity) => {
        if (isPresent(startDate)) {
          return moment(startDate).startOf('day').isSameOrBefore(locationsCapacity.capacityDate, 'day');
        }
        return true;
      })
      .filter((locationsCapacity) => {
        if (isPresent(endDate)) {
          return moment(endDate).endOf('day').isSameOrAfter(locationsCapacity.capacityDate, 'day');
        }
        return true;
      });
  });

  // **************************************************************************
  // Locations
  // **************************************************************************

  this.put('/a/visitors/api/v2/locations/:id/logo', function (schema, { params }) {
    this.timing = 1400;
    const { id } = params;
    const location = schema.locations.find(id);

    location.logo = { url: 'http://lorempixel.com/400/200' };
    location.save();
    return location;
  });
  this.post('/api/v1/locations/1/print_badges', function () {
    return new Mirage.Response(204);
  });

  this.post('/api/v1/locations/:id/invite_preview', async function () {
    const emailTemplate = {
      subject: 'sample subject',
      body: `<div> Sample body </div>`,
    };
    return emailTemplate;
  });

  this.post('/a/visitors/api/v2/locations', function (schema) {
    const attrs = this.normalizedRequestAttrs();
    if (!attrs.configId) {
      // The API generates a device config as a side effect of creating a location
      // If the UI test creates a new Location strictly from clicking on UI
      // We will need to assign a device config to this new location to avoid errors
      // eslint-disable-next-line no-console
      console.warn('A device config has been assigned for the location you have created');
      attrs.configId = 1;
    }
    return schema.locations.create(attrs);
  });

  this.get('/a/visitors/api/v2/locations');

  this.get('/a/visitors/api/v3/locations', function (schema, request) {
    let collection;

    const idParam = request.queryParams['filter[id]'];

    if (idParam) {
      collection = schema.locations.find(idParam.split(','));
    } else {
      collection = schema.locations.all();
    }

    return collection;
  });

  this.get('/a/visitors/api/v2/locations/:id');
  this.get('/a/visitors/api/v3/locations/:id');
  this.patch('/a/visitors/api/v3/locations/:id');
  this.patch('/a/visitors/api/v2/locations/:id', function (schema, { params }) {
    const { locations, locationsCapacities, visitorSurveyConfigurations } = schema;
    const { id } = params;
    const location = locations.find(id);
    const attrs = this.normalizedRequestAttrs();

    if (location.visitorSurveyEnabled === false && attrs.visitorSurveyEnabled === true) {
      location.flows.models.forEach((flow) => {
        flow.update({ visitorSurveyEnabled: true });
      });
      const surveyConfig = visitorSurveyConfigurations.create({ locationId: id });
      attrs.visitorSurveyConfigurationId = surveyConfig.id;
    }

    location.update(attrs);

    /**
     * Updating the location.capacityLimit also updates the capacityLimit of all future
     * locations-capacities unless location.capacityLimit is null
     */
    const { capacityLimit } = location;
    if (isPresent(capacityLimit)) {
      const now = moment().startOf('day');
      locationsCapacities
        .where({ locationId: id })
        .filter((locationsCapacity) => moment(locationsCapacity.capacityDate).startOf('day').isSameOrAfter(now))
        .update({ capacityLimit });
    }

    return new Response(204);
  });

  this.post('/a/visitors/api/v3/locations/:id/ping_request', function () {
    return new Mirage.Response(204);
  });

  this.get('/a/visitors/api/v2/locations/:id/calendar', function (schema) {
    const data = {};
    const payload = { data: [] };

    schema.entries.all().models.forEach(function (entry) {
      const date = moment(entry.signedInAt).format('YYYY-MM-DD');
      if (!data[date]) {
        data[date] = { date, entries: 0, invites: 0 };
      }

      data[date].entries++;
    });

    schema.invites.all().models.forEach(function (invite) {
      const date = moment(invite.expectedArrivalTime).format('YYYY-MM-DD');
      if (!data[date]) {
        data[date] = { date, entries: 0, invites: 0 };
      }

      data[date].invites++;
    });

    Object.keys(data).forEach(function (date) {
      const resource = { attributes: data[date], id: date, type: 'calendar-event-entries' };
      payload.data.push(resource);
    });

    return new Mirage.Response(200, {}, payload);
  });

  this.get('/a/visitors/api/v2/locations/:id/notification-preview', function () {
    return new Mirage.Response(
      200,
      {},
      {
        data: {
          attributes: { subject: '', body: '' },
        },
      },
    );
  });

  // **************************************************************************
  // Location roles
  // **************************************************************************
  this.get('/a/visitors/api/v2/location-roles', (schema, request) => {
    request.queryParams.include = 'location';

    let collection = new Collection('location-role');

    if (request.queryParams['filter[locations]']) {
      const locationIds = request.queryParams['filter[locations]'].split(',');
      locationIds.forEach(function (id) {
        collection.models.push(...schema.locationRoles.where({ locationId: window.parseInt(id) }).models);
      });
    } else {
      collection = schema.locationRoles.all();
      collection.modelName = 'location-role';
    }

    return collection;
  });
  this.post('/a/visitors/api/v2/location-roles');
  this.patch('/a/visitors/api/v2/location-roles/:id');
  this.delete('/a/visitors/api/v2/location-roles/:id');
  this.get('/a/visitors/api/v2/location-roles/:id', function (schema, request) {
    request.queryParams.include = 'user,location';

    return schema.locationRoles.find(request.params.id);
  });

  // **************************************************************************
  // Zone roles
  // **************************************************************************

  this.delete('/a/visitors/api/v2/zone-roles/:id');

  // **************************************************************************
  // Meetings
  // **************************************************************************
  this.get('/a/visitors/api/v3/meetings/:id');
  this.patch('/a/visitors/api/v3/meetings/:id');

  // **************************************************************************
  // Photo and Id scan pages/fields
  // **************************************************************************
  this.get('/a/visitors/api/v3/photo-pages/:id');
  this.get('/a/visitors/api/v3/photo-pages', filteredGet('photoPages'));
  this.patch('/a/visitors/api/v3/photo-pages/:id');
  this.get('/a/visitors/api/v3/id-scan-pages');
  this.get('/a/visitors/api/v3/id-scan-pages/:id');
  this.patch('/a/visitors/api/v3/id-scan-pages/:id');

  this.get('/a/visitors/api/v3/id-scan-fields');

  // **************************************************************************
  // Places Autocomplete
  // **************************************************************************
  this.get('/a/visitors/api/v3/places/autocomplete', function (schema, request) {
    const search = request.queryParams['filter[search]'];
    return { data: [{ id: '123', attributes: { description: `${search}, State, USA` } }] };
  });

  // **************************************************************************
  // Plans
  // **************************************************************************
  this.get('/a/visitors/api/v2/plans');
  this.get('/a/visitors/api/v2/plans/:id');

  // **************************************************************************
  // Platform jobs
  // **************************************************************************
  this.get('/a/visitors/api/v3/platform-jobs', function (schema, request) {
    const entryId = (request.queryParams['filter[entry]'] || '').split(',').filter((v) => !!v);
    const identifier = (request.queryParams['filter[identifier]'] || '').split(',').filter((v) => !!v);
    const where = {};
    if (entryId.length) {
      Object.assign(where, { entryId });
    }
    if (identifier.length) {
      Object.assign(where, { identifier });
    }
    if (Object.keys(where).length) {
      return schema.platformJobs.where(where);
    }
    return schema.platformJobs.all();
  });
  this.get('/a/visitors/api/v3/platform-jobs/:id');

  // **************************************************************************
  // Plugins
  // **************************************************************************
  this.get('/a/visitors/api/v2/plugins', function (schema, request) {
    request.queryParams.include = '';

    const collection = schema.plugins.all();

    return collection;
  });
  this.get('/a/visitors/api/v2/plugin-installs', function (schema, request) {
    let collection;
    if (request.queryParams['filter[status]']) {
      const status = request.queryParams['filter[status]'];
      collection = schema.pluginInstalls.where({ status });
    } else {
      collection = schema.pluginInstalls.all();
    }

    return paginatedResponse(this, collection, request);
  });
  this.patch('/a/visitors/api/v2/plugin-installs/:id');
  this.post('/a/visitors/api/v2/plugin-installs/:id/authorize-scopes', (schema, request) => {
    const install = schema.pluginInstalls.first({ id: request.params.id });
    this.db.pluginInstalls.update(request.params.id, {
      authorizedScopes: install.plugin.manifest.scopes,
    });
    return schema.pluginInstalls.first({ id: request.params.id });
  });

  // **************************************************************************
  // Printers
  // **************************************************************************
  this.get('/api/v1/printers');
  this.get('/api/v1/printers/:id');

  this.get('/a/visitors/api/v3/printers', function (schema, request) {
    // get location id from device config id
    const configId = request.queryParams['filter[device_config]'];
    let locationId = null;
    const visitorEntranceId = request.queryParams['filter[visitor_entrance]'];
    if (isBlank(visitorEntranceId)) {
      if (configId) {
        locationId = schema.locations.first({ configId }).id;
      } else {
        locationId = request.queryParams['filter[location]'];
      }
    }

    const offset = request.queryParams['page[offset]'];
    const limit = request.queryParams['page[limit]'];
    const connectionType = request.queryParams['filter[connectionType]'];

    const collection = schema.printers.all();
    let printers = collection.models;

    if (locationId) {
      printers = printers.filter((printer) => {
        return printer.location && printer.location.id === locationId;
      });
    }

    if (visitorEntranceId) {
      printers = printers.filter((printer) => {
        return printer.visitorEntrance && printer.visitorEntrance.id === visitorEntranceId;
      });
    }

    if (connectionType) {
      printers = printers.filter((printer) => {
        return printer.connectionType === connectionType;
      });
    }

    const totalCount = printers.length;

    // pagination
    if (offset && limit) {
      printers = printers.slice(offset, offset + limit);
    }

    // returns total count
    collection.models = printers;
    const response = this.serializerOrRegistry.serialize(collection, request);
    response.meta = {
      total: totalCount,
      models: collection.models.mapBy('model').uniq().compact(),
    };

    return new Mirage.Response(200, {}, response);
  });

  this.get('/a/visitors/api/v3/printers/:id');
  this.post('/a/visitors/api/v3/printers');
  this.patch('/a/visitors/api/v3/printers/:id');
  this.delete('/a/visitors/api/v3/printers/:id');

  this.get('/a/visitors/api/v3/property-printers', function (schema, request) {
    const zoneFilter = request.queryParams['filter[property]'];

    // If this request is looking for Bluetooth printers, it probably came from the "new printer" form.
    // To simulate a successful Bluetooth pairing, create a printer entry and return it so that the form
    // stops ceaselessly polling.
    if (request.queryParams['filter[connectionType]'] === 'bluetooth') {
      schema.printers.create({
        connectionType: 'bluetooth',
        propertyId: zoneFilter,
        status: 'connected',
      });
    }
    let printers = schema.printers.where((printer) => printer.propertyId);
    if (zoneFilter) {
      printers = printers.filter((printer) => printer.propertyId === zoneFilter);
    }
    const response = this.serializerOrRegistry.serialize(printers, request);

    response.included?.forEach((included) => {
      if (included.type === 'devices' && included.relationships.printer.data) {
        included.relationships.printer.data.type = 'property-printers';
      }
    });

    response.data.forEach((item) => {
      item.type = 'property-printers';
    });

    return response;
  });

  this.post('/a/visitors/api/v3/property-printers', function (schema, request) {
    const attrs = this.normalizedRequestAttrs();
    const printer = schema.printers.create(attrs);

    const response = this.serializerOrRegistry.serialize(printer, request);
    response.data.type = 'property-printers';

    return response;
  });

  this.patch('/a/visitors/api/v3/property-printers/:id', function (schema, request) {
    const printer = schema.printers.find(request.params.id);
    printer.update(this.normalizedRequestAttrs());
    const response = this.serializerOrRegistry.serialize(printer, request);

    response.data.type = 'property-printers';

    return response;
  });

  this.delete('/a/visitors/api/v3/property-printers/:id', function ({ printers }, { params }) {
    const printer = printers.find(params.id);
    printer.destroy();
    return new Response(204);
  });

  // **************************************************************************
  // Pubnub channels
  // **************************************************************************
  this.get('/a/visitors/api/v2/me/pubnub-channels', function (schema, request) {
    /* eslint-disable camelcase */
    let pubnubConfig;

    const locationId = request.queryParams.location_id;
    const zone_id = request.queryParams.zone_id;
    if (locationId) {
      const location = schema.locations.find(locationId);

      pubnubConfig = {
        cipherKey: location.pubnubCipherKey,
        channelEntry: location.pubnubChannelEntry,
        channelInvite: location.pubnubChannelInvite,
        channelIntegrations: location.pubnubChannelIntegrations,
        channelDevices: location.pubnubChannelDevices,
      };
    } else if (zone_id) {
      pubnubConfig = {
        channelConnectInvites: 'zone-connect-invites',
        channelDevices: 'zone-devices',
      };
    }

    return new Mirage.Response(
      200,
      {},
      {
        data: {
          attributes: pubnubConfig,
        },
      },
    );
  });

  // **************************************************************************
  // Protect Access Codes
  // **************************************************************************
  this.get('/a/visitors/api/v3/protect-access-codes');
  this.post('/a/visitors/api/v3/protect-access-codes');

  // **************************************************************************
  // Recurring Invites
  // **************************************************************************
  this.get('/a/visitors/api/v3/recurring-invites/:id');
  this.patch('/a/visitors/api/v3/recurring-invites/:id');
  this.post('/a/visitors/api/v3/recurring-invites');

  // **************************************************************************
  // Salesforce Leads
  // **************************************************************************
  this.post('/a/visitors/api/v3/salesforce-leads', {}, 204);

  // **************************************************************************
  // SAMLs
  // **************************************************************************
  this.get('/a/visitors/api/v3/samls');
  this.post('/a/visitors/api/v3/samls', function (schema, request) {
    const handler = new BaseShorthandRouteHandler(schema, this.serializerOrRegistry);

    // The backend uses the user's company to find out which company to modify
    const company = schema.companies.first();
    const saml = company.createSaml(handler._getAttrsForRequest(request, 'saml'));
    company.update({ samlConnected: true });

    return saml;
  });
  this.get('/a/visitors/api/v3/samls/:id');
  this.put('/a/visitors/api/v3/samls/:id');
  this.patch('/a/visitors/api/v3/samls/:id');
  this.delete('/a/visitors/api/v3/samls/:id');

  // **************************************************************************
  // Setup guides
  // **************************************************************************
  this.get('/a/visitors/api/v3/setup-guide-steps', function (schema, { queryParams }) {
    let steps = schema.setupGuideSteps.all();
    if (queryParams['filter[title]']) {
      const params = { title: queryParams['filter[title]'] };
      steps = schema.setupGuideSteps.where(params);
    }
    return steps;
  });
  this.get('/a/visitors/api/v3/locations-setup-guide-steps', function (schema, { queryParams }) {
    let steps = schema.locationsSetupGuideSteps.all();
    const params = {};
    if (queryParams['filter[location]']) {
      params.locationId = queryParams['filter[location]'];
    }
    if (queryParams['filter[setupGuideStep]']) {
      params.setupGuideStepId = queryParams['filter[setupGuideStep]'];
    }
    if (queryParams['filter[completed]']) {
      params.completed = queryParams['filter[completed]'];
    }
    if (Object.keys(params).length > 0) {
      steps = schema.locationsSetupGuideSteps.where(params);
    }
    return steps;
  });
  this.get('/a/visitors/api/v3/locations-setup-guide-steps/:id');
  this.patch('/a/visitors/api/v3/locations-setup-guide-steps/:id');
  this.post('/a/visitors/api/v3/locations-setup-guide-steps/:id/view', function (schema, request) {
    return schema.locationsSetupGuideSteps.find(request.params.id).update({
      firstViewedAt: new Date(),
    });
  });

  // **************************************************************************
  // Sign in fields
  // **************************************************************************
  this.get('/a/visitors/api/v3/sign-in-field-pages');
  this.get('/a/visitors/api/v3/sign-in-field-pages/:id');
  this.patch('/a/visitors/api/v3/sign-in-field-pages/:id');

  this.post('/a/visitors/api/v3/sign-in-fields');
  this.get('/a/visitors/api/v3/sign-in-fields', function (schema, request) {
    let collection;

    if (request.queryParams['filter[id]']) {
      collection = schema.signInFields.find(request.queryParams['filter[id]'].split(','));
    } else {
      collection = schema.signInFields.all();
    }

    return collection;
  });
  this.get('/a/visitors/api/v3/sign-in-fields/:id');
  this.patch('/a/visitors/api/v3/sign-in-fields/:id');
  this.delete('/a/visitors/api/v3/sign-in-fields/:id');

  this.post('/a/visitors/api/v3/sign-in-field-actions');
  this.patch('/a/visitors/api/v3/sign-in-field-actions/:id');
  this.delete('/a/visitors/api/v3/sign-in-field-actions/:id');

  this.get('/a/visitors/api/v3/sign-in-field-actions-contacts/:id');
  this.post('/a/visitors/api/v3/sign-in-field-actions-contacts');
  this.patch('/a/visitors/api/v3/sign-in-field-actions-contacts/:id');
  this.delete('/a/visitors/api/v3/sign-in-field-actions-contacts/:id');

  this.get('/a/visitors/api/v3/sign-in-field-action-rule-groups');
  this.get('/a/visitors/api/v3/sign-in-field-action-rule-groups/:id');
  this.post('/a/visitors/api/v3/sign-in-field-action-rule-groups');
  this.patch('/a/visitors/api/v3/sign-in-field-action-rule-groups/:id');
  this.delete('/a/visitors/api/v3/sign-in-field-action-rule-groups/:id');
  // **************************************************************************
  // Slideshows
  // **************************************************************************
  this.post('/a/visitors/api/v2/slideshows', function ({ slideshows, configs, welcomeScreenSlides }, request) {
    const propertyId = request.requestBody.get('slideshow[zone_id]');
    const locationId = request.requestBody.get('slideshow[location_id]');

    const slideshow = slideshows.create({ url: 'https://picsum.photos/400/200' });

    if (propertyId) {
      const [config] = configs.where({ propertyId }).models;
      welcomeScreenSlides.create({ image: 'https://picsum.photos/400/200', configId: config.id });
      slideshow.destroy();
    } else if (locationId) {
      const [config] = configs.where({ locationId }).models;
      slideshow.update({ configId: config.id });
    }

    // always return slideshow to serialize properly
    return slideshow;
  });
  this.get('/a/visitors/api/v2/slideshows/:id');
  this.delete('/a/visitors/api/v2/slideshows/:id');

  // **************************************************************************
  // Welcome Screen Slides
  // **************************************************************************

  this.delete('/a/visitors/api/v3/welcome-screen-slides/:id');

  // **************************************************************************
  // Summary pages
  // **************************************************************************
  this.get('/a/visitors/api/v3/summary-pages/:id');
  this.patch('/a/visitors/api/v3/summary-pages/:id');
  this.get('/a/visitors/api/v3/summary-pages', filteredGet('summaryPages'));
  this.post('/a/visitors/api/v3/summary-pages/:id/attachments/image', function ({ summaryPages }, request) {
    const id = request.params.id;
    return summaryPages.find(id).update({ image: 'http://lorempixel.com/400/200' });
  });

  this.get('/a/visitors/api/v3/devices', function ({ devices }, request) {
    const locationId = request.queryParams['filter[location]'];
    const zoneId = request.queryParams['filter[zone]'];

    let filteredDevices = devices.all();

    // [SQ-4293] Support both array param and comma-separated param for location, zone
    if (locationId) {
      const locations = Array.isArray(locationId) ? locationId : locationId.split(',');

      filteredDevices = filteredDevices.filter((device) => locations.includes(device.locationId));
    } else if (zoneId) {
      const zones = Array.isArray(zoneId) ? zoneId : zoneId.split(',');

      filteredDevices = filteredDevices.filter((device) => zones.includes(device.zoneId));
    }

    const response = this.serializerOrRegistry.serialize(filteredDevices, request);

    if (request.queryParams['page[offset]']) {
      response.data.splice(0, request.queryParams['page[offset]']);
    }

    if (request.queryParams['page[limit]']) {
      response.data.splice(request.queryParams['page[limit]'], response.data.length);
    }

    response.meta.count = response.data.length;
    response.meta.total = filteredDevices.length;

    return new Mirage.Response(200, {}, response);
  });

  // **************************************************************************
  // Device config static pages
  // **************************************************************************

  this.get('/a/visitors/api/v3/device-config-static-pages', function ({ deviceConfigStaticPages }, request) {
    const propertyId = request.queryParams['filter[property]'];
    const pages = deviceConfigStaticPages.where({ propertyId });

    const response = this.serializerOrRegistry.serialize(pages, request);

    response.data.forEach((record) => {
      record.type = record.attributes.type;
    });

    return response;
  });

  this.patch('/a/visitors/api/v3/device-config-static-page-attributes/:id', async function (schema, request) {
    const id = request.params.id;
    const attrs = this.normalizedRequestAttrs();

    if (attrs.image) {
      attrs.image = await getImageUrlForFileID(schema, attrs.image);
    }

    return schema.deviceConfigStaticPageAttributes.find(id).update(attrs);
  });

  this.post('/a/visitors/api/v3/device-config-static-page-attribute-translations');
  this.patch('/a/visitors/api/v3/device-config-static-page-attribute-translations/:id');

  // **************************************************************************
  // Sync batches
  // **************************************************************************
  this.get('/a/visitors/api/v2/sync-batches', function (schema, request) {
    const locationId = request.queryParams['filter[location]'];
    const records = schema.syncBatches.where({ locationId });
    return records;
  });

  // **************************************************************************
  // Users
  // **************************************************************************
  this.get('/a/visitors/api/v2/users');

  this.get('/a/visitors/api/v3/users/:id', (schema, request) => {
    const user = schema.users.find(request.params['id']);
    const employee = schema.employees.findBy({ name: user.fullName });
    const formattedEmployee = {
      id: employee.id,
      attributes: {
        'hide-from-host-list': employee.hideFromHostList,
        name: employee.name,
        email: employee.email,
        'company-id': schema.companies.first().id,
      },
      type: 'employees',
      relationships: {},
    };
    return { data: user.data, included: [formattedEmployee] };
  });

  this.get('/a/visitors/api/v2/users/:id', (schema, request) => {
    request.queryParams.include = 'company-roles,location-roles';

    return schema.users.find(request.params.id);
  });

  this.patch('/a/visitors/api/v2/users/:id');

  this.patch('/a/visitors/api/v3/users/:id', async function (schema, request) {
    const { id } = request.params;
    const attrs = this.normalizedRequestAttrs();
    const { userPhoto } = attrs;

    if (userPhoto) {
      attrs.userPhotoUrl = await getImageUrlForFileID(schema, userPhoto);
    } else if (userPhoto === null) {
      attrs.userPhotoUrl = null;
    }

    delete attrs.userPhoto;

    return schema.users.find(id).update(attrs);
  });

  this.get('/a/visitors/api/v2/users/lookup', (schema, { queryParams }) => {
    const { email } = queryParams;

    if (email) {
      return {
        data: {
          id: email,
          type: 'user-lookups',
          attributes: {
            'uses-credentials': true,
            'uses-identity': false,
            'identity-saml': false,
            confirmed: true,
            'passport-only': false,
          },
        },
      };
    }

    return new Response(
      404,
      { 'Content-Type': 'application/json; charset=utf-8' },
      {
        errors: [
          {
            status: '404',
            title: 'Bad request',
            detail: 'user email not found',
            source: { parameter: 'email' },
          },
        ],
      },
    );
  });

  this.get('/a/visitors/api/v2/users/me', (schema, request) => {
    // always include company-roles, location-roles, and zone-roles
    request.queryParams.include = 'company-roles,location-roles,zone-roles,emails';

    return schema.users.find(1);
  });

  this.post('/a/visitors/api/v3/users/accept_privacy_policy', () => {
    return new Response(204);
  });

  // **************************************************************************
  // Role assignments
  // **************************************************************************
  this.get('/a/visitors/api/v2/role-assignments', function ({ roleAssignments }, request) {
    const userIdFilter = request.queryParams['filter[user-id]'];
    if (userIdFilter) {
      return roleAssignments.where({ userId: userIdFilter });
    }
    return roleAssignments.all();
  });
  this.post('/a/visitors/api/v2/role-assignments');
  this.patch('/a/visitors/api/v2/role-assignments/:id');
  this.delete('/a/visitors/api/v2/role-assignments/:id');

  this.get('/a/visitors/api/v2/role-assignments/count', function ({ roleAssignments }) {
    return roleAssignments.all().models.reduce((counts, roleAssignment) => {
      counts[roleAssignment.roleId] ||= { user_count: 0 };
      counts[roleAssignment.roleId].user_count++;
      return counts;
    }, {});
  });

  this.post('/a/visitors/api/v2/role-assignments/bulk', function (schema, request) {
    const data = JSON.parse(request.requestBody).data;

    // first, remove all role assignments for the role ID/user ID/scope type combination at scope IDs that weren't included
    const rolesToRemove = schema.roleAssignments
      .where({
        userId: data['user-id'],
        roleId: data['role-id'],
        roleScopeType: data['role-scope-type'],
      })
      .models.filter((roleAssignment) => !data['role-scope-ids'].includes(roleAssignment.roleScopeId));

    rolesToRemove.forEach((role) => role.destroy());

    const roles = data['role-scope-ids'].map((id) =>
      schema.roleAssignments.findOrCreateBy({
        userId: data['user-id'],
        roleId: data['role-id'],
        roleScopeId: id,
        roleScopeType: data['role-scope-type'],
      }),
    );
    return schema.roleAssignments.find(roles.map((roleAssignment) => roleAssignment.id));
  });

  this.post('/a/visitors/api/v2/role-assignments/bulk-destroy', function ({ roleAssignments }, request) {
    const body = JSON.parse(request.requestBody);
    const idsToDelete = body.data['role-assignment-ids'];
    const rolesToDelete = roleAssignments.find(idsToDelete);
    rolesToDelete.destroy();
    return new Response(200);
  });

  // **************************************************************************
  // User roles
  // **************************************************************************
  this.get('/a/visitors/api/v2/user-roles', (schema, request) => {
    // The API includes these automatically
    request.queryParams.include = 'user';

    let collection = new Collection('user-role');

    const locationParam = request.queryParams['filter[location]'];

    if (locationParam) {
      const locationIds = locationParam.split(',');
      const companyIds = this.db.locations.find(locationIds).mapBy('companyId').uniq();

      locationIds.forEach(function (id) {
        collection.models.push(...schema.locationRoles.where({ locationId: window.parseInt(id) }).models);
      });

      companyIds.forEach(function (id) {
        collection.models.push(...schema.companyRoles.where({ companyId: window.parseInt(id) }).models);
      });
    } else {
      collection = schema.companyRoles.all();

      collection.models.push(...schema.locationRoles.all().models);
      collection.models.push(...schema.zoneRoles.all().models);

      collection.modelName = 'user-role';
    }

    const nameFilter = request.queryParams['filter[name]'];
    if (nameFilter) {
      const nameRegex = new RegExp(nameFilter, 'i');
      collection.models = collection.models.filter((role) => nameRegex.test(role.user.fullName));
    }

    return collection;
  });

  // **************************************************************************
  // User roles digests
  // **************************************************************************
  this.get('/a/visitors/api/v2/user-roles-digests', (schema, request) => {
    const locationFilter = request.queryParams['filter[location]'];
    const nameFilter = request.queryParams['filter[name]'];
    const rolesFilter = request.queryParams['filter[roles]'];

    let filteredRoles = [];
    if (rolesFilter) {
      filteredRoles = rolesFilter.split(',');
    }

    const userToRolesMapping = {};

    const users = schema.users.all().models;
    for (const user of users) {
      if (nameFilter && !user.fullName.includes(nameFilter)) continue;

      const roles = {};

      let hasManuallyAddedRole = true;
      let hasSyncedRole = false;
      const roleAssignments = schema.roleAssignments.where({ userId: user.id });
      for (const roleAssignment of roleAssignments.models) {
        const role = schema['authz/roles'].find(roleAssignment.roleId);
        // when filtering by role name, legacy role names are passed as the filter values
        if (role && filteredRoles.length > 0 && !filteredRoles.includes(getLegacyRoleName(role.name))) continue;

        if (roleAssignment.roleScopeType === 'location' && locationFilter) {
          const locationIds = locationFilter.split(',');
          if (!locationIds.includes(roleAssignment.roleScopeId)) continue;
        }

        if (!roleAssignment.manuallyAdded) {
          hasManuallyAddedRole = false;
          hasSyncedRole = true;
        }

        roles[role.name] ||= [];
        roles[role.name].push({
          id: roleAssignment.roleScopeId,
          name: roleAssignment.roleScopeName,
          type: pluralize(roleAssignment.roleScopeType),
        });
      }

      if (Object.keys(roles).length > 0) {
        userToRolesMapping[user.id] = {
          id: user.id,
          type: 'user-roles-digest',
          attributes: {
            email: user.email,
            'full-name': user.fullName,
            'last-seen': user._lastSeenAt ?? faker.date.past(),
            avatar: user.avatar,
            'user-photo-url': user.userPhotoUrl,
            'has-synced-role': hasSyncedRole,
            'has-manually-added-role': hasManuallyAddedRole,
            roles,
          },
        };
      }
    }

    const response = {
      data: Object.values(userToRolesMapping),
    };

    return new Response(200, {}, response);
  });

  // **************************************************************************
  // Versions (Changelog)
  // **************************************************************************
  this.get('/a/visitors/api/v3/versions', function (schema, request) {
    const offset = request.queryParams['page[offset]'];
    const limit = request.queryParams['page[limit]'];

    let changelogs = schema.changelogs.all();
    const totalCount = changelogs.length;

    // pagination
    if (offset && limit) {
      changelogs = changelogs.slice(offset, offset + limit);
    }

    const response = this.serializerOrRegistry.serialize(changelogs, request);
    response.meta = { total: totalCount, limit, offset };

    return new Mirage.Response(200, {}, response);
  });
  this.get('/a/visitors/api/v3/versions/:id', 'changelog');

  // **************************************************************************
  // Visitor Guide
  // **************************************************************************
  this.get('/a/visitors/api/v3/locations/:id/welcome-email-preview', function (_schema, _request) {
    const htmlToRender = `
    <!DOCTYPE html>
    <html>
      <body>
        <h1>Email preview</h1>
      </body>
    </html>`;
    return new Mirage.Response(
      201,
      { 'Content-Type': 'application/json' },
      { data: { attributes: { body: htmlToRender, subject: 'Your visit' } } },
    );
  });

  // **************************************************************************
  // Visitor Guide
  // **************************************************************************
  this.get('/a/visitors/api/v3/visitor-guides/:id');
  this.post('/a/visitors/api/v3/visitor-guides', function (schema, request) {
    const { data } = JSON.parse(request.requestBody);
    const locationId = data.relationships.location.data.id;
    const location = schema.locations.find(locationId);
    let visitorGuide = location.visitorGuide;
    if (!visitorGuide) {
      // Create visitor guide and sections if one did not exist
      visitorGuide = schema.visitorGuides.create({ locationId });
      schema.visitorGuideSections.create({ title: 'Connecting to Wi-Fi', visitorGuide });
      schema.visitorGuideSections.create({ title: 'Finding a restroom', visitorGuide });
    }
    return visitorGuide;
  });
  this.post('/a/visitors/api/v3/visitor-guides/:id/preview', function (_schema, request) {
    let body = JSON.parse(request.requestBody);
    let sections = body.data.relationships['visitor-guide-sections'];
    sections = sections.sortBy('data.attributes.position');
    body = `
      <!DOCTYPE html>
      <html>
        <body style="margin: 60px;background-color: white;">
          <h1>Preview</h1>
          <ul>`;

    sections.forEach(({ data: { attributes } }) => {
      if (attributes.enabled && isPresent(attributes.body)) {
        body += `<li>${attributes.title}: ${attributes.body}</li>`;
      }
    });

    body += `
          <ul>
        </body>
      </html>`;
    return new Mirage.Response(201, { 'Content-Type': 'application/json' }, { body });
  });
  this.get('/a/visitors/api/v3/visitor-guide-sections');
  this.get('/a/visitors/api/v3/visitor-guide-sections/:id');
  this.patch('/a/visitors/api/v3/visitor-guide-sections/:id');

  // **************************************************************************
  // Visitor survey
  // **************************************************************************
  this.post('/a/visitors/api/v3/visitor-survey-configurations');
  this.get('/a/visitors/api/v3/visitor-survey-configurations/:id');
  this.patch('/a/visitors/api/v3/visitor-survey-configurations/:id');
  this.get('/a/visitors/api/v3/visitor-survey-configurations/:id/preview', function (_schema, _request) {
    const body = `
      <!DOCTYPE html>
      <html>
        <body style="margin: 60px; background-color: white;">
          <h1>Survey Webview Preview</h1>
          <ul>
            <li>Survey Questions...</li>
          <ul>
        </body>
      </html>`;
    return new Mirage.Response(201, { 'Content-Type': 'application/json' }, { body });
  });
  this.get('/a/visitors/api/v3/visitor-survey-configurations/:id/email-survey-preview', function (_schema, _request) {
    const body = `
      <!DOCTYPE html>
      <html>
        <body>
          <h1>Survey Email Preview</h1>
        </body>
      </html>`;
    const subject = 'Sample subject';
    return new Mirage.Response(201, { 'Content-Type': 'application/json' }, { body, subject });
  });
  this.get(
    '/a/visitors/api/v3/visitor-survey-configurations/:id/analytics-email-preview',
    function (_schema, _request) {
      const body = `
    <!DOCTYPE html>
    <html>
      <body>
        <h1>Survey Results Email Preview</h1>
      </body>
    </html>`;
      return new Mirage.Response(201, { 'Content-Type': 'application/json' }, { body });
    },
  );

  // **************************************************************************
  // Visual Compliance Configurations
  // **************************************************************************
  this.post('/a/visitors/api/v3/visual-compliance-configurations');
  this.patch(
    '/a/visitors/api/v3/visual-compliance-configurations/:id',
    function ({ visualComplianceConfigurations }, request) {
      const config = visualComplianceConfigurations.find(request.params.id);
      const attrs = this.normalizedRequestAttrs();
      attrs.credentialsInvalid = false; // the backend will validate the secno/password and set this flag
      delete attrs.password; // the backend never sends the password in responses
      return config.update(attrs);
    },
  );
  this.get('/a/visitors/api/v3/visual-compliance-configurations/:id');

  // **************************************************************************
  // Groups
  // **************************************************************************
  this.get('/a/visitors/api/v3/groups');
  this.post('/a/visitors/api/v3/groups');
  this.delete('/a/visitors/api/v3/groups/:id');
  this.patch('/a/visitors/api/v3/groups/:id');

  // **************************************************************************
  // Visitor entrances
  // **************************************************************************

  this.get('/a/visitors/api/v3/visitor-entrances', function (schema, request) {
    const locationFilter = request.queryParams['filter[location]'];

    const records = schema.visitorEntrances.all();

    if (locationFilter) {
      const locationIds = locationFilter.split(',');
      return records.filter((visitorEntrance) => {
        return locationIds.includes(visitorEntrance.locationId);
      });
    }

    return records;
  });
  this.get('/a/visitors/api/v3/visitor-entrances/:id');
  this.post('/a/visitors/api/v3/visitor-entrances');
  this.delete('/a/visitors/api/v3/visitor-entrances/:id');
  this.patch('/a/visitors/api/v3/visitor-entrances/:id');

  // **************************************************************************
  // Webhooks
  // **************************************************************************
  this.get('/api/v1/webhooks');
  this.post('/api/v1/webhooks');
  this.get('/api/v1/webhooks/:id');
  this.put('/api/v1/webhooks/:id');
  this.delete('/api/v1/webhooks/:id');

  // **************************************************************************
  // Banners
  // **************************************************************************
  this.get('/a/visitors/api/v3/banners');
  this.post('/a/visitors/api/v3/banners/:id/dismiss', function (schema, request) {
    // request body uses { location_id } for custom dismissal behavior on backend
    const banner = schema.banners.find(request.params.id);
    if (banner) {
      banner.destroy();
      return new Mirage.Response(200);
    } else {
      return new Mirage.Response(304);
    }
  });

  // **************************************************************************
  // Global Flows
  // **************************************************************************
  this.get('/a/visitors/api/v3/global-flows', function (schema, request) {
    let collection;

    const offset = parseInt(request.queryParams['page[offset]'], 10);
    const limit = parseInt(request.queryParams['page[limit]'], 10);
    const idParam = request.queryParams['filter[id]'];

    if (idParam) {
      collection = schema.globalFlows.find(idParam.split(','));
    } else {
      collection = schema.globalFlows.all();
    }

    const response = this.serializerOrRegistry.serialize(collection, request);

    if (!idParam) {
      const totalCount = collection.length;

      if (offset && limit) {
        collection = collection.slice(offset, offset + limit);
      }

      response.meta = { total: totalCount, limit, offset };
    }

    return new Mirage.Response(200, {}, response);
  });

  this.post('/a/visitors/api/v3/global-flows');
  this.get('/a/visitors/api/v3/global-flows/:id');
  this.patch('/a/visitors/api/v3/global-flows/:id');

  // **************************************************************************
  // Atomic Operations
  // **************************************************************************
  this.post('/api/v3/operations', function () {
    return new Mirage.Response(200);
  });

  // **************************************************************************
  // Visitors Mailers
  // **************************************************************************
  this.get('/a/visitors/api/v3/mailer-templates', function ({ mailerTemplates }, { queryParams }) {
    const envoyDefault = queryParams['filter[envoy-default]'] === 'true';
    const locationId = queryParams['filter[location-id]'];

    if (envoyDefault) return mailerTemplates.where({ envoyDefault });

    let collection = mailerTemplates.all();

    if (locationId) {
      collection = collection.filter((mailerTemplate) => {
        return mailerTemplate.envoyDefault || mailerTemplate.locationIds.includes(locationId);
      });
    }

    return collection;
  });

  this.get('/a/visitors/api/v3/mailer-templates/:id', async function (schema, request) {
    const mailerTemplate = schema.mailerTemplates.find(request.params.id);
    const locationId = request.queryParams['meta[location_id]'];
    const location = locationId ? schema.locations.find(locationId) : null;
    const response = this.serializerOrRegistry.serialize(mailerTemplate, request);
    const includedMailerBlocks = response.included?.filterBy('type', 'mailer-blocks') ?? [];

    if (location?.address) {
      includedMailerBlocks.forEach((block) => {
        const attrs = block.attributes?.settings?.attributes ?? [];

        attrs.forEach((attr) => {
          if (attr.address) {
            attr.address.value = location.address;
          }
        });
      });
    }

    return new Response(200, {}, response);
  });

  this.post('/a/visitors/api/v3/mailer-templates', async function ({ mailerTemplates }) {
    const attrs = this.normalizedRequestAttrs();

    return mailerTemplates.create({ ...attrs, ...{ active: true } });
  });

  this.patch('/a/visitors/api/v3/mailer-templates/:id');
  this.delete('a/visitors/api/v3/mailer-templates/:id');
  this.patch('/a/visitors/api/v3/mailer-templates/:id/preview', async function () {
    const emailTemplate = {
      subject: 'A Tale of Two Cities',
      body: `
      <!DOCTYPE html>
      <html>
        <body>
          <div>It was the test of times, it was the worst of times</div>
        </body>
      </html>`,
    };
    return emailTemplate;
  });
  this.patch('/a/visitors/api/v3/mailer-templates/:id/send-test', async function () {
    return new Mirage.Response(204);
  });

  this.post('/a/visitors/api/v3/mailer-blocks');
  this.patch('/a/visitors/api/v3/mailer-blocks/:id');
  this.delete('a/visitors/api/v3/mailer-blocks/:id');

  // **************************************************************************
  // Documents
  // **************************************************************************

  this.post('/a/visitors/api/v3/user-document-links', function ({ userDocumentLinks }, request) {
    const body = JSON.parse(request.requestBody);

    return userDocumentLinks.create({
      userDocumentId: body.data.relationships['user-document'].data,
      approvalStatus: 'review',
      // userDocumentLinkableId: body.data.relationships['user-document-linkable'].data,
    });
  });

  this.get('/a/visitors/api/v3/user-document-links', function ({ userDocumentLinks }, request) {
    const approvalStatusFilter = request.queryParams['filter[approval-status]'];
    const templateIdentifierFilter = request.queryParams['filter[user-document-template-identifier]'];

    let collection = userDocumentLinks.all();

    if (approvalStatusFilter) {
      collection = collection.filter((userDocumentLink) => userDocumentLink.approvalStatus === approvalStatusFilter);
    }

    if (templateIdentifierFilter) {
      collection = collection.filter(
        (userDocumentLink) =>
          userDocumentLink?.userDocument?.userDocumentTemplate?.identifier === templateIdentifierFilter,
      );
    }

    const response = this.serializerOrRegistry.serialize(collection, request);
    response.meta = {
      total: collection.models.length,
    };
    return response;
  });
  this.get('/a/visitors/api/v3/user-document-links/:id');
  this.patch('/a/visitors/api/v3/user-document-links/:id');

  this.post('/a/visitors/api/v3/user-document-links/:id/approve', function ({ userDocumentLinks }, { params }) {
    const userDocumentLink = userDocumentLinks.find(params.id);
    userDocumentLink.update({ approvalStatus: 'approved' });
    return new Response(204);
  });
  this.post('/a/visitors/api/v3/user-document-links/:id/deny', function ({ userDocumentLinks }, { params }) {
    const userDocumentLink = userDocumentLinks.find(params.id);
    userDocumentLink.update({ approvalStatus: 'denied' });
    return new Response(204);
  });
  this.post('/a/visitors/api/v3/user-document-links/:id/review', function ({ userDocumentLinks }, { params }) {
    const userDocumentLink = userDocumentLinks.find(params.id);
    userDocumentLink.update({ approvalStatus: 'review' });
    return new Response(204);
  });

  this.get('/a/visitors/api/v3/user-document-attachments/:id');
  this.get('/a/visitors/api/v3/user-document-template-attachments/');
  this.get('/a/visitors/api/v3/user-document-template-attachments/:id');
  this.get('/a/visitors/api/v3/user-document-templates/:id');
  this.get('/a/visitors/api/v3/user-document-templates', function ({ userDocumentTemplates }, request) {
    const identifierFilter = request.queryParams['filter[identifier]'];
    if (identifierFilter) {
      return userDocumentTemplates.where({ identifier: identifierFilter });
    }
    return userDocumentTemplates.all();
  });

  this.get('/a/visitors/api/v3/user-documents', function ({ userDocuments }, request) {
    const userFilter = request.queryParams['filter[user]'];
    const templateIdentifierFilter = request.queryParams['filter[user-document-template-identifier]'];
    let collection = userDocuments.all();

    if (userFilter) {
      collection = collection.filter((userDocument) => userDocument.userId === userFilter);
    }
    if (templateIdentifierFilter) {
      collection = collection.filter(
        (userDocument) => userDocument?.userDocumentTemplate?.identifier === templateIdentifierFilter,
      );
    }

    return collection;
  });

  this.post('/a/visitors/api/v3/user-documents', async function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const { relationships } = body.data;
    const { attachments } = body.data.attributes;

    delete body.data.attributes.attachments;
    delete body.data.relationships;

    const newAttachments = await Promise.all(
      attachments.map(async (attachment) => {
        const fileUrl = await getImageUrlForFileID(schema, attachment.file);

        const attrs = {
          fileUrl,
          userDocumentTemplateAttachmentId: attachment['user-document-template-attachment'],
        };

        return schema.userDocumentAttachments.create(attrs);
      }),
    );

    const userDocument = schema.userDocuments.create({
      ...body.data.attributes,
      createdAt: new Date(),
      userId: relationships.user.data.id,
      userDocumentTemplateId: relationships['user-document-template'].data.id,
      userDocumentAttachmentIds: newAttachments.map((attachment) => attachment.id),
    });

    return userDocument;
  });

  this.patch('/a/visitors/api/v3/user-document-attachments/:id', async (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const { id } = request.params;

    const record = schema.userDocumentAttachments.find(id);
    const fileUrl = await getImageUrlForFileID(schema, body.data.attributes.file);

    record.update({ fileUrl });

    return record;
  });

  this.post('/a/visitors/api/v3/user-document-attachments', async (schema, request) => {
    const body = JSON.parse(request.requestBody);
    const { relationships } = body.data;

    const fileUrl = await getImageUrlForFileID(schema, body.data.attributes.file);

    const attrs = {
      fileUrl,
      userDocumentTemplateAttachmentId: relationships['user-document-template-attachment'].data.id,
      userDocumentId: relationships['user-document'].data.id,
    };

    const attachment = schema.userDocumentAttachments.create(attrs);
    return attachment;
  });

  this.delete('/a/visitors/api/v3/user-document-attachments/:id');

  this.get('/a/visitors/api/v3/user-documents/:id');
  this.delete('/a/visitors/api/v3/user-documents/:id');
  this.patch('/a/visitors/api/v3/user-documents/:id');

  this.get(
    '/a/visitors/api/v3/user-document-template-configurations',
    function ({ userDocumentTemplateConfigurations, flows }, request) {
      const activeFilter = request.queryParams['filter[active]'];
      const locationIdFilter = request.queryParams['filter[location-id]'];
      const flowIdFilter = request.queryParams['filter[flow-id]'];
      const userDocumentTemplateIdFilter = request.queryParams['filter[user-document-template-id]'];

      const filters = {};

      // The lookup methods in mirage don't work with nested keys in objects. Need to use custom lookup callback.
      const compareConfig = (attrs) => {
        return (record) =>
          (record.userDocumentTemplateId === attrs.userDocumentTemplateId || !attrs.userDocumentTemplateId) &&
          record.userDocumentTemplateConfigurableId.id === attrs.userDocumentTemplateConfigurableId.id &&
          record.userDocumentTemplateConfigurableId.type === attrs.userDocumentTemplateConfigurableId.type;
      };

      if (flowIdFilter) {
        // flow polymorphic search
        const flow = flows.find(flowIdFilter);

        const attributes = {
          userDocumentTemplateId: userDocumentTemplateIdFilter,
          userDocumentTemplateConfigurableId: {
            type: 'flow',
            id: flow.id,
          },
        };

        filters['userDocumentTemplateConfigurableId'] = { id: flowIdFilter, type: 'flow' };

        const flowConfiguration = userDocumentTemplateConfigurations.where(compareConfig(attributes));

        if (!flowConfiguration.models.length) {
          userDocumentTemplateConfigurations.create({ ...attributes, active: false, optional: true });
        }
      }

      if (activeFilter) {
        filters['active'] = activeFilter;
      }
      if (locationIdFilter) {
        // location polymorphic search
        filters['userDocumentTemplateConfigurableId'] = { id: locationIdFilter, type: 'location' };
      }
      if (userDocumentTemplateIdFilter) {
        filters['userDocumentTemplateId'] = userDocumentTemplateIdFilter;
      }
      if (locationIdFilter && userDocumentTemplateIdFilter) {
        const attributes = {
          userDocumentTemplateId: userDocumentTemplateIdFilter,
          userDocumentTemplateConfigurableId: { id: locationIdFilter, type: 'location' },
        };
        const locationConfiguration = userDocumentTemplateConfigurations.where(compareConfig(attributes));

        if (!locationConfiguration.models.length) {
          userDocumentTemplateConfigurations.create(attributes);
        }
      }

      return userDocumentTemplateConfigurations.where(compareConfig(filters));
    },
  );
  this.get('/a/visitors/api/v3/user-document-template-configurations/:id');
  this.patch('/a/visitors/api/v3/user-document-template-configurations/:id');

  this.post(
    '/a/visitors/api/v3/user-document-template-configurations/:id/deny_all',
    function ({ userDocumentLinks }, request) {
      const body = JSON.parse(request.requestBody);
      const userDocumentTemplateConfigurationId = request.params.id;

      userDocumentLinks.all().models.forEach((link) => {
        const { userDocument } = link;
        const { userDocumentTemplate } = userDocument;
        const { userDocumentTemplateConfigurations } = userDocumentTemplate;

        const matchingConfiguration = userDocumentTemplateConfigurations.models.find(
          (config) => config.id === userDocumentTemplateConfigurationId,
        );

        if (
          matchingConfiguration &&
          isBefore(userDocument.createdAt, parse(body['end-date'], 'yyyy-MM-dd', new Date()))
        ) {
          link.update({ approvalStatus: 'denied' });
        }
      });
    },
  );

  this.get('/a/visitors/api/v3/visitor-documents/:id');
  this.delete('/a/visitors/api/v3/visitor-documents/:id');
  this.patch('/a/visitors/api/v3/visitor-documents/:id');

  this.get('/a/visitors/api/v3/visitor-documents', function ({ visitorDocuments, invites }, request) {
    const templateIdentifierFilter = request.queryParams['filter[user-document-template-identifier]'];
    const inviteFilter = request.queryParams['filter[invite-id]'];
    const entryFilter = request.queryParams['filter[entry-id]'];
    let collection = visitorDocuments.all();

    if (templateIdentifierFilter) {
      collection = collection.filter(
        (visitorDocument) => visitorDocument?.userDocumentTemplate?.identifier === templateIdentifierFilter,
      );
    }

    if (inviteFilter) {
      const invite = invites.find(inviteFilter);

      // If the invite is a recurring child, search by recurring ID
      if (invite?.recurringInviteId) {
        collection = collection.filter((visitorDocument) =>
          visitorDocument?.recurringInviteIds?.includes(invite.recurringInviteId),
        );
      } else {
        collection = collection.filter((visitorDocument) => visitorDocument?.inviteIds?.includes(inviteFilter));
      }
    }

    if (entryFilter) {
      collection = collection.filter((visitorDocument) => visitorDocument?.entryIds?.includes(entryFilter));
    }

    return collection;
  });

  this.post('/a/visitors/api/v3/visitor-documents', async function (schema, request) {
    const body = JSON.parse(request.requestBody);
    const { relationships } = body.data;
    const { attachments } = body.data.attributes;

    delete body.data.attributes.attachments;
    delete body.data.relationships;

    const newAttachments = await Promise.all(
      attachments.map(async (attachment) => {
        const file = await getFilenameForFileID(schema, attachment.file);
        const fileUrl = await getImageUrlForFileID(schema, attachment.file);

        const attrs = {
          file,
          fileUrl,
          userDocumentTemplateAttachmentId: attachment['user-document-template-attachment'],
        };

        return schema.userDocumentAttachments.create(attrs);
      }),
    );

    const entryIds = relationships.entries?.data?.mapBy('id') ?? [];
    const inviteIds = relationships.invites?.data?.mapBy('id') ?? [];
    const recurringInviteIds = relationships['recurring-invites']?.data?.mapBy('id') ?? [];
    const locationIds = relationships.locations?.data?.mapBy('id') ?? [];

    const visitorDocument = schema.visitorDocuments.create({
      ...body.data.attributes,
      createdAt: new Date(),
      entryIds,
      inviteIds,
      recurringInviteIds,
      locationIds,
      userDocumentTemplateId: relationships['user-document-template'].data.id,
      userDocumentAttachmentIds: newAttachments.map((attachment) => attachment.id),
    });

    return visitorDocument;
  });

  this.delete('/a/visitors/api/v3/visitor-documents/:id/relationships/:model_type', (schema, request) => {
    const { id, model_type } = request.params;
    const visitorDocument = schema.visitorDocuments.find(id);
    const { data } = JSON.parse(request.requestBody);

    if (!visitorDocument) return new Mirage.Response(404);

    const removeIds = data.filter((i) => i.type === model_type).map((i) => i.id);
    const models = visitorDocument[model_type].models.filter(({ id }) => !removeIds.includes(id));

    visitorDocument.update({
      [model_type]: {
        models,
      },
    });

    return new Mirage.Response(204);
  });

  this.get('/a/visitors/api/v3/blacklist-contact-users');
  this.post('/a/visitors/api/v3/blacklist-contact-users');
  this.delete('/a/visitors/api/v3/blacklist-contact-users/:id');

  // **************************************************************************
  // CSV uploads
  // **************************************************************************
  this.post('/a/visitors/api/v3/csv-uploads', function (schema, request) {
    const requestData = JSON.parse(request.requestBody.get('data'));
    const destination = requestData.attributes.destination;
    const csvUploadable = requestData.relationships['csv-uploadable'];
    const attrs = { destination };
    const baseFilterAttrs = {};
    if (csvUploadable?.data?.id === 'zones') {
      attrs.csvUploadable = schema.zones.find(csvUploadable?.data?.id);
      baseFilterAttrs.zone = attrs.csvUploadable;
    }

    const file = request.requestBody.get('file');
    Papa.parse(file, {
      complete(results) {
        results.data.forEach((row) => {
          // array attributes like emailAddresses and phoneNumbers ignored for simplicity
          const filterAttrs = { fullName: row[1], description: row[2], reason: row[3] };
          schema.blacklistFilters.create({ ...baseFilterAttrs, ...filterAttrs });
        });
      },
    });
    return schema.csvUploads.create(attrs);
  });

  this.get('/a/visitors/api/v3/csv-uploads/:id/upload-status', function () {
    return {
      data: {
        attributes: {
          'imported-count': 100,
          message: 'Success',
          'total-count': 100,
          'upload-status': 'done',
        },
      },
    };
  });

  // **************************************************************************
  // Ticketing
  // **************************************************************************
  this.post('/a/visitors/api/v3/ticket-configurations');
  this.patch('/a/visitors/api/v3/ticket-configurations/:id');
  this.get('/a/visitors/api/v3/ticket-configurations', function ({ ticketConfigurations }, { queryParams }) {
    const locationId = queryParams['filter[location]'];
    let collection = ticketConfigurations.all();
    if (isPresent(locationId)) {
      collection = collection.filter((ticketConfiguration) => locationId === ticketConfiguration.locationId);
    }
    return collection;
  });
  this.post('/a/visitors/api/v3/ticket-categories');
  this.delete('/a/visitors/api/v3/ticket-categories/:id');
  this.post('/a/visitors/api/v3/ticket-category-emails');
  this.delete('/a/visitors/api/v3/ticket-category-emails/:id');

  // **************************************************************************
  // Location / Employee Registration Configuration
  // **************************************************************************
  this.patch('/a/visitors/api/v3/employee-registration-configurations/:id');
  this.get('/a/visitors/api/v3/employee-registration-configurations/:id');
  this.get(
    '/a/visitors/api/v3/employee-registration-configurations',
    function ({ employeeRegistrationConfigurations }, { queryParams }) {
      const locationId = queryParams['filter[location-id]'];

      // Find or create
      if (isPresent(locationId)) {
        const employeeRegistrationConfiguration = employeeRegistrationConfigurations.where({ locationId });

        if (!employeeRegistrationConfiguration.models.length) {
          employeeRegistrationConfigurations.create({ locationId });
        }
      }

      return employeeRegistrationConfigurations.where({ locationId });
    },
  );

  // **************************************************************************
  // Connect Location Configurations
  // **************************************************************************

  this.get(
    '/a/visitors/api/v3/connect-location-configurations',
    function ({ connectLocationConfigurations }, { queryParams }) {
      const locationId = queryParams['filter[location]'];
      let configuration = connectLocationConfigurations.findBy({ locationId });
      if (!configuration)
        configuration = connectLocationConfigurations.create({
          locationId,
          automaticEntryCreationOnSignIn: false,
          canEnableEmployeeRebadging: true,
          canEnableEmployeePropertyMobileCredentialing: true,
          canEnableVisitorPropertyDigitalPass: true,
        });

      const collection = new Collection('connect-location-configuration');
      collection.models.push(configuration);
      return collection;
    },
  );
  this.patch('/a/visitors/api/v3/connect-location-configurations/:id');

  // **************************************************************************
  // Connect Property Configurations
  // **************************************************************************

  this.get(
    '/a/visitors/api/v3/connect-property-configurations',
    function ({ connectPropertyConfigurations }, { queryParams }) {
      const propertyId = queryParams['filter[property]'];
      return connectPropertyConfigurations.where({ propertyId });
    },
  );

  this.patch('/a/visitors/api/v3/connect-property-configurations/:id');

  // **************************************************************************
  // Expanded Printer Support (Printer Connections)
  // **************************************************************************

  this.get('/a/visitors/api/v3/printer-connections', function ({ locations, printerConnections }, { queryParams }) {
    if (queryParams['filter[location-id]']) {
      const location = locations.find(queryParams['filter[location-id]']);
      const globalPrinterConnections = printerConnections.where({ companyId: location.companyId });
      const localPrinterConnections = printerConnections.where({ locationId: location.id });
      return localPrinterConnections.mergeCollection(globalPrinterConnections);
    } else if (queryParams['filter[zone-id]']) {
      return printerConnections.where({ zoneId: queryParams['filter[zone-id]'] });
    } else {
      return printerConnections.all();
    }
  });
  this.get('/a/visitors/api/v3/printer-connections/:id');
  this.delete('/a/visitors/api/v3/printer-connections/:id');

  this.get('/a/visitors/api/v3/printer-connection-options', function (schema, { queryParams }) {
    const isInUseExcluded = queryParams['filter[exclude-in-use]'];
    let collection = schema.printerConnectionOptions.all();

    const printerConnectionIdFilter = queryParams['filter[printer-connection-id]'];
    if (printerConnectionIdFilter) {
      const printerConnection = schema.printerConnections.find(printerConnectionIdFilter);
      collection = collection.filter((opt) => opt.parentId === printerConnection.externalResourceUid);
    }

    if (isInUseExcluded) {
      const locationIdFilter = queryParams['filter[location-id]'];
      const zoneIdFilter = queryParams['filter[zone-id]'];
      let printers = schema.printers.all();
      if (locationIdFilter) {
        printers = printers.filter((printer) => printer.locationId === locationIdFilter);
      } else if (zoneIdFilter) {
        printers = printers.filter((printer) => printer.propertyId === zoneIdFilter);
      }
      const extUids = printers.models.mapBy('externalResourceUid');

      collection = collection.filter((opt) => !extUids.includes(opt.id));
    }

    return collection;
  });
  this.get('/a/visitors/api/v3/printer-connection-options/:id');

  // Request One-time Passcode
  this.post('/a/visitors/api/v3/printer-passcodes', function () {
    // Use factory to create a new one-time passcode
    // eslint-disable-next-line no-undef
    const record = server.create('printer-passcode', {
      ...this.normalizedRequestAttrs(),
      factoryRecord: true, // Enable simulated code use
    });

    return record;
  });

  // Polling endpoint
  this.get('/a/visitors/api/v3/printer-passcodes/:id');

  // This is not a "real" endpoint. It enables "simulated" passcode use.
  this.patch('/a/visitors/api/v3/printer-passcodes/:id', function ({ printerPasscodes }, request) {
    const id = request.params.id;
    const consumedAt = new Date();

    // eslint-disable-next-line no-undef
    const printerConnection = server.create('printer-connection', 'withOptions');

    printerPasscodes.find(id).update({ consumedAt, printerConnection });

    return new Mirage.Response(204);
  });

  // **************************************************************************
  // "Static" QR Codes / BYOD Registration URLs
  // **************************************************************************

  this.get('/a/visitors/api/v3/paper-devices');
  this.get('/a/visitors/api/v3/paper-devices/:id');
  this.post('/a/visitors/api/v3/paper-devices', function () {
    const { locationId, printerId, name } = this.normalizedRequestAttrs();
    const data = { locationId };

    if (printerId) data['printerId'] = printerId;
    if (name) data['name'] = name;

    // eslint-disable-next-line no-undef
    const record = server.create('paper-device', data);

    return record;
  });
  this.patch('/a/visitors/api/v3/paper-devices/:id');
  this.delete('/a/visitors/api/v3/paper-devices/:id');

  // **************************************************************************
  // Notification Config
  // **************************************************************************
  this.get('/a/visitors/api/v3/locations/:id/notification-configuration', function () {
    return {
      data: {
        id: 1,
        type: 'notification-configuration',
        attributes: {
          'emergency-notifications-enabled-at': null,
        },
      },
    };
  });

  // **************************************************************************
  // Emergency Notification Config
  // **************************************************************************
  this.get('/a/visitors/api/v3/locations/:id/emergency-notification-configuration', function () {
    return {
      data: {
        id: 1,
        type: 'emergency-notification-configuration',
        attributes: {
          'emergency-notifications-enabled-at': null,
          'passive-phone-number-collection-enabled-at': null,
        },
      },
    };
  });

  this.patch('/a/visitors/api/v3/emergency-notification-configurations/:id', function () {
    const attrs = this.normalizedRequestAttrs();
    return {
      data: {
        id: attrs.id,
        type: 'emergency-notification-configuration',
        attributes: {
          'emergency-notifications-enabled-at': attrs.emergencyNotificationsEnabledAt,
          'passive-phone-number-collection-enabled-at': attrs.passivePhoneNumberCollectionEnabledAt,
        },
      },
    };
  });

  // **************************************************************************
  // Group Invites
  // **************************************************************************
  this.get('/a/visitors/api/v3/group-invites');
  this.get('/a/visitors/api/v3/group-invites/:id');
  this.post('/a/visitors/api/v3/group-invites');
  this.patch('/a/visitors/api/v3/group-invites/:id');
  this.delete('/a/visitors/api/v3/group-invites/:id');
  this.post('/a/visitors/api/v3/group-invites/:id/notify', {}, 204);

  // **************************************************************************
  // Virtual Front Desk
  // **************************************************************************
  this.get('/a/visitors/api/v3/vfd-configurations', function ({ vfdConfigurations }, request) {
    let configurations = sortCollection(vfdConfigurations.all(), request.queryParams['sort']);

    const enabledFilter = request.queryParams['filter[enabled]'];
    const queryFilter = request.queryParams['filter[query]'];

    if (enabledFilter) {
      const enabledFilterBool = enabledFilter === 'true';
      configurations = configurations.filter((config) => config.enabled === enabledFilterBool);
    }

    if (queryFilter) {
      configurations = configurations.filter((config) => {
        return config.locationName.toLowerCase().includes(queryFilter.toLowerCase());
      });
    }

    return paginatedResponse(this, configurations, request);
  });
  this.get('/a/visitors/api/v3/vfd-configurations/:id');
  this.patch('/a/visitors/api/v3/vfd-configurations/:id');
  this.post('/a/visitors/api/v3/vfd-contact-methods');
  this.patch('/a/visitors/api/v3/vfd-contact-methods/:id');
  this.get(
    '/a/visitors/api/v3/company/:id/vfd-company-configuration',
    function ({ vfdCompanyConfigurations }, request) {
      return vfdCompanyConfigurations.findBy({
        companyId: request.params.id,
      });
    },
  );
  this.patch('/a/visitors/api/v3/vfd-company-configurations/:id');

  this.get('/a/visitors/api/v3/vfd-call-logs', function ({ vfdCallLogs }, request) {
    const records = sortCollection(vfdCallLogs.all(), request.queryParams['sort']);
    return paginatedResponse(this, records, request);
  });
  this.get('/a/visitors/api/v3/vfd-call-logs/:id');

  this.patch('/a/visitors/api/v3/vfd-schedules/:id');

  /// **************************************************************************
  // Communications
  // **************************************************************************
  this.get('/a/visitors/api/v3/announcement-templates');
  this.get('/a/visitors/api/v3/announcement-templates/:id');
  this.post('/a/visitors/api/v3/announcement-templates');
  this.patch('/a/visitors/api/v3/announcement-templates/:id');
  this.delete('/a/visitors/api/v3/announcement-templates/:id');

  this.get('/a/visitors/api/v3/announcement-template-categories');
  this.post('/a/visitors/api/v3/announcement-template-categories');

  this.get('/a/visitors/api/v3/announcement-audiences');
  this.get('/a/visitors/api/v3/announcement-audiences/:id');

  this.get('/a/visitors/api/v3/announcement-channels');
  this.get('/a/visitors/api/v3/announcement-channels/:id');

  this.get('/a/visitors/api/v3/announcements');
  this.get('/a/visitors/api/v3/announcements/:id');

  this.get('/a/visitors/api/v3/announcement-recipients/:id');
  this.get('/a/visitors/api/v3/announcement-recipients', function ({ announcementRecipients }, request) {
    const records = sortCollection(announcementRecipients.all(), request.queryParams['sort']);
    return paginatedResponse(this, records, request);
  });
}
